import React, { useState, useEffect, useRef } from 'react';
import { Drawer, Button, Descriptions, Tag, Space, Typography, Divider, Input, Select, Row, Col, Card, Spin, Alert } from 'antd';
import { 
  EyeOutlined, 
  UserOutlined, 
  MailOutlined, 
  PhoneOutlined, 
  CarOutlined,
  NumberOutlined,
  DollarOutlined,
  CreditCardOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  CloseOutlined,
  ShopOutlined,
  TagOutlined,
  EditOutlined,
  SaveOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  WalletOutlined,
  GlobalOutlined,
  IdcardOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import API from '../../API/API';
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { X } from 'lucide-react';
import Logo from "../../JPEG/Logo.png";

const { Title, Text } = Typography;
const { Option } = Select;

// Modern styled components using inline styles
const styles = {
  drawerHeader: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '16px 24px',
    borderRadius: '12px 12px 0 0',
  },
  sectionCard: {
    background: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    marginBottom: '24px',
    padding: '20px',
    border: '1px solid #f0f0f0',
    transition: 'all 0.3s ease',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#2d3748',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: '2px solid #667eea',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  infoRow: {
    display: 'flex',
    marginBottom: '12px',
    padding: '8px 12px',
    background: '#f8f9fa',
    borderRadius: '8px',
    alignItems: 'center',
  },
  label: {
    fontSize: '13px',
    color: '#718096',
    width: '100px',
    fontWeight: 500,
  },
  value: {
    fontSize: '14px',
    color: '#2d3748',
    fontWeight: 500,
    flex: 1,
  },
  editInput: {
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '14px',
    transition: 'all 0.3s ease',
    width: '100%',
  },
  badge: {
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 600,
  },
  successBadge: {
    background: '#c6f6d5',
    color: '#22543d',
  },
  primaryBadge: {
    background: '#667eea',
    color: 'white',
  },
  cardDetails: {
    background: '#f0f5ff',
    padding: '16px',
    borderRadius: '12px',
    marginTop: '12px',
    border: '1px solid #d6e4ff',
  }
};

// Enhanced Popup Message Component
const PopupMessage = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const getIcon = () => {
    switch(type) {
      case 'success': return <CheckCircleOutlined style={{ fontSize: '20px' }} />;
      case 'error': return <CloseCircleOutlined style={{ fontSize: '20px' }} />;
      default: return <InfoCircleOutlined style={{ fontSize: '20px' }} />;
    }
  };

  const getBackgroundColor = () => {
    switch(type) {
      case 'success': return 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)';
      case 'error': return 'linear-gradient(135deg, #f56565 0%, #e53e3e 100%)';
      default: return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: '24px',
      right: '24px',
      zIndex: 10000,
      animation: 'slideIn 0.3s ease, fadeOut 0.3s ease 2.7s',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
    }}>
      <div style={{
        background: getBackgroundColor(),
        color: 'white',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        minWidth: '320px',
      }}>
        {getIcon()}
        <span style={{ flex: 1, fontSize: '14px', fontWeight: 500 }}>{message}</span>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            fontSize: '14px',
            padding: '4px 8px',
            borderRadius: '6px',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
          onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
        >
          ✕
        </button>
      </div>
    </div>
  );
};

const LeadDetailsDrawer = ({ visible, onClose, leadData, onLeadUpdate, leadId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [popup, setPopup] = useState({ show: false, message: '', type: 'error' });
  const [refreshing, setRefreshing] = useState(false);
  const [currentLeadData, setCurrentLeadData] = useState(leadData);
const [showPdfViewer, setShowPdfViewer] = useState(false);
const [isDownloading, setIsDownloading] = useState(false);
const receiptRef = useRef();
const page2Ref = useRef();
const page3Ref = useRef();
const page4Ref = useRef();
const page5Ref = useRef();
const page6Ref = useRef();
const page7Ref = useRef();
  // Fetch latest lead data by ID
  const fetchLeadById = async (id) => {
    if (!id) return;
    
    setIsRefreshing(true);
    try {
      const response = await API.GetLeadById(id);
      if (response.status && response.data) {
        setCurrentLeadData(response.data);
        setEditedData(response.data);
        setOriginalData(response.data);
      }
    } catch (error) {
      console.error('Error fetching lead details:', error);
      showPopup('Error refreshing lead data', 'error');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Update currentLeadData when leadData prop changes
  useEffect(() => {
    if (leadData) {
      setCurrentLeadData(leadData);
      setEditedData(leadData);
      setOriginalData(leadData);
    }
  }, [leadData]);

  const showPopup = (message, type = 'error') => {
    setPopup({ show: true, message, type });
  };

  const hidePopup = () => {
    setPopup({ show: false, message: '', type: 'error' });
  };

  if (!currentLeadData) return null;

  const ZOHO_TARGET_PDF_SIZE_MB = 20;
  const ZOHO_MAX_PDF_SIZE_MB = 25;
  const getPdfSizeInMb = (pdf) => {
    const pdfArrayBuffer = pdf.output('arraybuffer');
    return pdfArrayBuffer.byteLength / (1024 * 1024);
  };

  const waitForPdfRenderReady = async (pageRefs = [], timeoutMs = 2000) => {
    let timeoutId;
    const readinessPromise = (async () => {
      if (document.fonts?.ready) {
        try {
          await document.fonts.ready;
        } catch (error) {
          console.warn('PDF font readiness wait failed:', error);
        }
      }

      const images = pageRefs.flatMap(ref =>
        Array.from(ref.current?.querySelectorAll?.('img') || [])
      );

      await Promise.all(images.map(img => {
        if (img.complete) return Promise.resolve();

        return new Promise(resolve => {
          const done = () => {
            img.removeEventListener('load', done);
            img.removeEventListener('error', done);
            resolve();
          };

          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        });
      }));
    })();

    const timeoutPromise = new Promise(resolve => {
      timeoutId = setTimeout(resolve, timeoutMs);
    });

    try {
      await Promise.race([readinessPromise, timeoutPromise]);
    } finally {
      clearTimeout(timeoutId);
    }

    await new Promise(resolve => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
  };

  const fullName = currentLeadData.personalInfo?.fullName || '';
  const nameParts = fullName.split(' ');
  
  // For display: show firstName and lastName from either personalInfo or split fullName
  const displayFirstName = currentLeadData.personalInfo?.firstName || nameParts[0] || '';
  const displayLastName = currentLeadData.personalInfo?.lastName || nameParts.slice(1).join(' ') || '';
  
  // For editing: use editedData values
  const firstName = isEditing ? editedData?.personalInfo?.firstName || displayFirstName : displayFirstName;
  const lastName = isEditing ? editedData?.personalInfo?.lastName || displayLastName : displayLastName;
  
  const paymentMethod = currentLeadData.advancePayments?.[0]?.paymentMethod || 'N/A';
  const businessName = isEditing ? editedData?.bussinessName || 'N/A' : currentLeadData.bussinessName || 'N/A';

  const detectCardType = (number) => {
    const cleaned = number?.replace(/\s/g, '');
    const cardPatterns = {
      'American Express': /^3[47]/,
      'Visa': /^4/,
      'Mastercard': /^5[1-5]/,
      'Discover': /^6(?:011|5[0-9]{2})/,
      'Diners Club': /^3(?:0[0-5]|[68])/,
      'JCB': /^(?:2131|1800|35\d{3})/,
      'UnionPay': /^62/,
      'Maestro': /^(5[0-9]{1}|6[0-9]{1}|9[0-9]{1})/
    };

    for (const [cardType, regex] of Object.entries(cardPatterns)) {
      if (regex.test(cleaned)) {
        return cardType;
      }
    }
    return 'Unknown';
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedData(currentLeadData);
  };

  // Function to get only changed fields
  const getChangedFields = () => {
    const changes = {};
    
    // Compare order fields
    if (editedData.orderNumber !== originalData.orderNumber) {
      changes.orderNumber = editedData.orderNumber;
    }
    if (editedData.totalAmount !== originalData.totalAmount) {
      changes.totalAmount = editedData.totalAmount;
    }
    if (editedData.warranty !== originalData.warranty) {
      changes.warranty = editedData.warranty;
    }
    if (editedData.bussinessName !== originalData.bussinessName) {
      changes.bussinessName = editedData.bussinessName;
    }
    if ((editedData.attnName || '') !== (originalData.attnName || '')) {
      changes.attnName = editedData.attnName;
    }

    // Compare personalInfo fields - including fullName generation
    if (JSON.stringify(editedData.personalInfo) !== JSON.stringify(originalData.personalInfo)) {
      changes.personalInfo = {};
      if (editedData.personalInfo?.firstName !== originalData.personalInfo?.firstName) {
        changes.personalInfo.firstName = editedData.personalInfo?.firstName;
      }
      if (editedData.personalInfo?.lastName !== originalData.personalInfo?.lastName) {
        changes.personalInfo.lastName = editedData.personalInfo?.lastName;
      }
      if (editedData.personalInfo?.email !== originalData.personalInfo?.email) {
        changes.personalInfo.email = editedData.personalInfo?.email;
      }
      if (editedData.personalInfo?.phoneNumber !== originalData.personalInfo?.phoneNumber) {
        changes.personalInfo.phoneNumber = editedData.personalInfo?.phoneNumber;
      }
      
      // Generate fullName from firstName and lastName if either changed
      if (editedData.personalInfo?.firstName !== originalData.personalInfo?.firstName ||
          editedData.personalInfo?.lastName !== originalData.personalInfo?.lastName) {
        const newFirstName = editedData.personalInfo?.firstName || displayFirstName;
        const newLastName = editedData.personalInfo?.lastName || displayLastName;
        changes.personalInfo.fullName = `${newFirstName} ${newLastName}`.trim();
      }
    }

    // Compare billingAddress fields
    if (JSON.stringify(editedData.billingAddress) !== JSON.stringify(originalData.billingAddress)) {
      changes.billingAddress = {};
      if (editedData.billingAddress?.street !== originalData.billingAddress?.street) {
        changes.billingAddress.street = editedData.billingAddress?.street;
      }
      if (editedData.billingAddress?.city !== originalData.billingAddress?.city) {
        changes.billingAddress.city = editedData.billingAddress?.city;
      }
      if (editedData.billingAddress?.state !== originalData.billingAddress?.state) {
        changes.billingAddress.state = editedData.billingAddress?.state;
      }
      if (editedData.billingAddress?.zip !== originalData.billingAddress?.zip) {
        changes.billingAddress.zip = editedData.billingAddress?.zip;
      }
    }

    // Compare shippingAddress fields
    if (JSON.stringify(editedData.shippingAddress) !== JSON.stringify(originalData.shippingAddress)) {
      changes.shippingAddress = {};
      if (editedData.shippingAddress?.street !== originalData.shippingAddress?.street) {
        changes.shippingAddress.street = editedData.shippingAddress?.street;
      }
      if (editedData.shippingAddress?.city !== originalData.shippingAddress?.city) {
        changes.shippingAddress.city = editedData.shippingAddress?.city;
      }
      if (editedData.shippingAddress?.state !== originalData.shippingAddress?.state) {
        changes.shippingAddress.state = editedData.shippingAddress?.state;
      }
      if (editedData.shippingAddress?.zip !== originalData.shippingAddress?.zip) {
        changes.shippingAddress.zip = editedData.shippingAddress?.zip;
      }
    }

    // Compare vehicleInfo fields
    if (JSON.stringify(editedData.vehicleInfo) !== JSON.stringify(originalData.vehicleInfo)) {
      changes.vehicleInfo = {};
      if (editedData.vehicleInfo?.year !== originalData.vehicleInfo?.year) {
        changes.vehicleInfo.year = editedData.vehicleInfo?.year;
      }
      if (editedData.vehicleInfo?.make !== originalData.vehicleInfo?.make) {
        changes.vehicleInfo.make = editedData.vehicleInfo?.make;
      }
      if (editedData.vehicleInfo?.model !== originalData.vehicleInfo?.model) {
        changes.vehicleInfo.model = editedData.vehicleInfo?.model;
      }
      if (editedData.vehicleInfo?.part !== originalData.vehicleInfo?.part) {
        changes.vehicleInfo.part = editedData.vehicleInfo?.part;
      }
    }

    // Compare advancePayments fields
    if (JSON.stringify(editedData.advancePayments) !== JSON.stringify(originalData.advancePayments)) {
      changes.advancePayments = editedData.advancePayments;
    }

    return changes;
  };

  const handleSaveEdit = async () => {
    // Validate warranty first
    if (!editedData?.warranty || editedData.warranty === 'N/A' || editedData.warranty === '') {
      showPopup('Please select a warranty option before saving', 'error');
      return;
    }

    setIsSaving(true);
    
    try {
      // Get only changed fields for logging purposes
      const changedFields = getChangedFields();
      
      // If no changes, just exit edit mode
      if (Object.keys(changedFields).length === 0) {
        showPopup('No changes to save', 'info');
        setIsEditing(false);
        setIsSaving(false);
        return;
      }

      // Prepare complete payload with ALL fields (including unchanged ones)
      const completePayload = {
        _id: editedData._id,
        orderNumber: editedData.orderNumber,
        totalAmount: editedData.totalAmount || editedData.paidAmount,
        paidAmount: editedData.paidAmount,
        billingAddress: editedData.billingAddress,
        shippingAddress: editedData.shippingAddress,
        bussinessName: editedData.bussinessName,
        attnName: editedData.attnName || '',
        warranty: editedData.warranty,
        personalInfo: {
          ...editedData.personalInfo,
          firstName: editedData.personalInfo?.firstName || firstName,
          lastName: editedData.personalInfo?.lastName || lastName,
          fullName: `${editedData.personalInfo?.firstName || firstName} ${editedData.personalInfo?.lastName || lastName}`.trim()
        },
        vehicleInfo: editedData.vehicleInfo,
        advancePayments: editedData.advancePayments
      };

      console.log('Saving complete data (including unchanged fields):', completePayload);
      console.log('Changed fields only:', changedFields);

      // Call API to update the lead with complete data
      const response = await API.UpdateLead(editedData._id, completePayload);
      
      if (response.status) {
        // Success - update localStorage with full data
        try {
          const savedData = localStorage.getItem('editedLeadData');
          const parsedData = savedData ? JSON.parse(savedData) : {};
          parsedData[editedData._id] = editedData;
          localStorage.setItem('editedLeadData', JSON.stringify(parsedData));
        } catch (storageError) {
          console.error('Error saving to localStorage:', storageError);
        }

        showPopup('Changes saved successfully!', 'success');
        setIsEditing(false);
        
        // Show refreshing indicator
        setRefreshing(true);
        
        // Refresh data by calling getById API
        await fetchLeadById(editedData._id);
        
        // Call the callback to update parent component
        if (onLeadUpdate) {
          await onLeadUpdate(editedData);
        }
        
        // Hide refreshing after a short delay
        setTimeout(() => setRefreshing(false), 500);
      } else {
        showPopup(response.message || 'Error saving changes', 'error');
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      showPopup('Error saving changes. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    setEditedData(prev => {
      if (section === 'order') {
        return {
          ...prev,
          [field]: value
        };
      } else if (section === 'personal') {
        return {
          ...prev,
          personalInfo: {
            ...prev.personalInfo,
            [field]: value
          }
        };
      } else if (section === 'billingAddress') {
        return {
          ...prev,
          billingAddress: {
            ...prev.billingAddress,
            [field]: value
          }
        };
      } else if (section === 'shippingAddress') {
        return {
          ...prev,
          shippingAddress: {
            ...prev.shippingAddress,
            [field]: value
          }
        };
      } else if (section === 'vehicleInfo') {
        return {
          ...prev,
          vehicleInfo: {
            ...prev.vehicleInfo,
            [field]: value
          }
        };
      } else {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: value
          }
        };
      }
    });
  };

  const handlePaymentMethodChange = (value) => {
    setEditedData(prev => {
      const updatedLead = { ...prev };
      if (!updatedLead.advancePayments) {
        updatedLead.advancePayments = [{}];
      }
      updatedLead.advancePayments[0] = {
        ...updatedLead.advancePayments[0],
        paymentMethod: value
      };
      return updatedLead;
    });
  };

const handlePreviewPDF = () => {
  setShowPdfViewer(true);
};

  const currentData = isEditing ? editedData : currentLeadData;

  const renderField = (label, value, icon, field, section = null) => {
    const displayValue = value || 'N/A';
    
    if (isEditing && section) {
      return (
        <div style={styles.infoRow}>
          <span style={styles.label}>{icon} {label}</span>
          {section === 'order' ? (
            field === 'warranty' ? (
              <select
                style={styles.editInput}
                value={currentData?.warranty || 'N/A'}
                onChange={(e) => handleInputChange(section, field, e.target.value)}
              >
                <option value="N/A">N/A</option>
                <option value="30 Days">30 Days</option>
                <option value="60 Days">60 Days</option>
                <option value="90 Days">90 Days</option>
                <option value="120 Days">120 Days</option>
                <option value="150 Days">150 Days</option>
                <option value="180 Days">180 Days</option>
                <option value="1 Year">1 Year</option>
                <option value="2 Years">2 Years</option>

                <option value="3 Years">3 Years</option>
                <option value="4 Years">4 Years</option>
                <option value="5 Years">5 Years</option>
                <option value="No Warranty">No Warranty</option>
              </select>
            ) : (
              <input
                type={field === 'totalAmount' ? 'number' : 'text'}
                style={styles.editInput}
                value={currentData?.[field] || ''}
                onChange={(e) => handleInputChange(section, field, e.target.value)}
                placeholder={`Enter ${label}`}
              />
            )
          ) : section === 'personal' ? (
            <input
              type="text"
              style={styles.editInput}
              value={currentData?.personalInfo?.[field] || ''}
              onChange={(e) => handleInputChange(section, field, e.target.value)}
              placeholder={`Enter ${label}`}
            />
          ) : section === 'billingAddress' || section === 'shippingAddress' ? (
            <input
              type="text"
              style={styles.editInput}
              value={currentData?.[section]?.[field] || ''}
              onChange={(e) => handleInputChange(section, field, e.target.value)}
              placeholder={`Enter ${label}`}
            />
          ) : section === 'vehicleInfo' ? (
            <input
              type="text"
              style={styles.editInput}
              value={currentData?.vehicleInfo?.[field] || ''}
              onChange={(e) => handleInputChange(section, field, e.target.value)}
              placeholder={`Enter ${label}`}
            />
          ) : null}
        </div>
      );
    }

    return (
      <div style={styles.infoRow}>
        <span style={styles.label}>{icon} {label}</span>
        <span style={styles.value}>{displayValue}</span>
      </div>
    );
  };

const renderPageToPdf = async (pdf, element, pdfWidth, pdfHeight, jpegQuality = 0.7) => {
  if (!element) return;

  const prevWidth = element.style.width;
  const prevHeight = element.style.height;
  element.style.width = '800px';
  element.style.height = '1131px'; // ← same fixed height

  const canvas = await html2canvas(element, {
    scale: 1.25,
    useCORS: true,
    scrollY: -window.scrollY,
    width: 800,
    height: 1131,
    windowWidth: 800,
    backgroundColor: '#ffffff',
  });

  element.style.width = prevWidth;
  element.style.height = prevHeight;

  const imgData = canvas.toDataURL('image/jpeg', jpegQuality);
  pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
};

const handleDownload = async () => {
  setIsDownloading(true);
  try {
    await new Promise(resolve => setTimeout(resolve, 800));

    const allRefs = [receiptRef, page2Ref, page3Ref, page4Ref, page5Ref, page6Ref,page7Ref];
    const validPages = allRefs.filter(ref => ref && ref.current && ref.current instanceof HTMLElement);
    
    if (validPages.length === 0) {
      showPopup('No pages found to generate PDF.', 'error');
      return;
    }

    const generatePdfForQuality = async (jpegQuality) => {
      const attemptPdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });
      const attemptWidth = attemptPdf.internal.pageSize.getWidth();
      const attemptHeight = attemptPdf.internal.pageSize.getHeight();

      for (let i = 0; i < validPages.length; i++) {
        if (i > 0) attemptPdf.addPage();
        await renderPageToPdf(attemptPdf, validPages[i].current, attemptWidth, attemptHeight, jpegQuality);
      }

      return {
        pdf: attemptPdf,
        pdfSizeMB: getPdfSizeInMb(attemptPdf),
      };
    };

    let pdfResult = await generatePdfForQuality(0.7);

    if (pdfResult.pdfSizeMB > ZOHO_TARGET_PDF_SIZE_MB) {
      pdfResult = await generatePdfForQuality(0.6);
    }

    if (pdfResult.pdfSizeMB > ZOHO_MAX_PDF_SIZE_MB) {
      showPopup("PDF is larger than Zoho Sign's 25 MB limit. Please reduce document size and try again.", 'error');
      return;
    }

    pdfResult.pdf.save(`${currentLeadData?.orderNumber || 'ORDER'}.pdf`);

    if (pdfResult.pdfSizeMB > ZOHO_TARGET_PDF_SIZE_MB) {
      showPopup(`PDF downloaded at ${pdfResult.pdfSizeMB.toFixed(2)} MB. It is above the 20 MB target but still under Zoho's 25 MB limit.`, 'info');
      return;
    }

    showPopup(`PDF downloaded successfully! (${pdfResult.pdfSizeMB.toFixed(2)} MB)`, 'success');
  } catch (err) {
    console.error('PDF generation error:', err);
    showPopup('Error generating PDF. Please try again.', 'error');
  } finally {
    setIsDownloading(false);
  }
};
const PDFViewerContent = () => {

  const paymentMethod = currentLeadData?.advancePayments?.[0]?.paymentMethod || 'N/A';


  // Pages 2–6 only render when the ordered part is an engine
  const isEngine = (currentLeadData?.vehicleInfo?.part || '').toLowerCase().includes('engine');
  const isTransmission = (currentLeadData?.vehicleInfo?.part || '').toLowerCase().includes('transmission');
  const isTransferCase = (currentLeadData?.vehicleInfo?.part || '').toLowerCase().includes('transfer case');
const partName = (currentLeadData?.vehicleInfo?.part || '').toLowerCase();

const axleKeywords = [
  'axle',
  'rear',
  'differential',
  'carrier'
];

const isAxle = axleKeywords.some(keyword => partName.includes(keyword)); 
const isAbs = partName.includes('abs');
const isSteering = partName.includes('steering');
const isAirbag = partName.includes('airbag') || partName.includes('air bag') ;
const isDifferential = partName.includes('differential');

/* ── PDF helpers ──────────────────────────────────────────────── */
const renderPageToPdf = async (pdf, element, pdfWidth, pdfHeight, jpegQuality = 0.7) => {
  // Guard against null elements
  if (!element) return;

  const prevWidth = element.style.width;
  element.style.width = '800px';
  const canvas = await html2canvas(element, {
    scale: 1.25,
    useCORS: true,
    scrollY: -window.scrollY,
    width: 800,
    windowWidth: 800,
    backgroundColor: '#ffffff',
  });
  element.style.width = prevWidth;
  const imgData = canvas.toDataURL('image/jpeg', jpegQuality);
  const imgHeight = (canvas.height / canvas.width) * pdfWidth;
  if (imgHeight <= pdfHeight) {
    pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, imgHeight);
  } else {
    const scaledWidth = (pdfHeight / imgHeight) * pdfWidth;
    pdf.addImage(imgData, 'JPEG', (pdfWidth - scaledWidth) / 2, 0, scaledWidth, pdfHeight);
  }
};

const handleDownload = async () => {
  setIsDownloading(true);
  try {
    const allRefs = [receiptRef, page2Ref, page3Ref, page4Ref, page5Ref, page6Ref,page7Ref];
    const validPages = allRefs.filter(ref => ref && ref.current && ref.current instanceof HTMLElement);

    console.log('Valid pages found:', validPages.length); // debug

    if (validPages.length === 0) {
      showPopup('No pages found to generate PDF.', 'error');
      return;
    }

    await waitForPdfRenderReady(validPages);

    const generatePdfForQuality = async (jpegQuality) => {
      const attemptPdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });
      const attemptWidth = attemptPdf.internal.pageSize.getWidth();
      const attemptHeight = attemptPdf.internal.pageSize.getHeight();

      for (let i = 0; i < validPages.length; i++) {
        if (i > 0) attemptPdf.addPage();
        await renderPageToPdf(attemptPdf, validPages[i].current, attemptWidth, attemptHeight, jpegQuality);
      }

      return {
        pdf: attemptPdf,
        pdfSizeMB: getPdfSizeInMb(attemptPdf),
      };
    };

    let pdfResult = await generatePdfForQuality(0.7);

    if (pdfResult.pdfSizeMB > ZOHO_TARGET_PDF_SIZE_MB) {
      pdfResult = await generatePdfForQuality(0.6);
    }

    if (pdfResult.pdfSizeMB > ZOHO_MAX_PDF_SIZE_MB) {
      showPopup("PDF is larger than Zoho Sign's 25 MB limit. Please reduce document size and try again.", 'error');
      return;
    }

    pdfResult.pdf.save(`${currentLeadData.orderNumber || 'ORDER'}.pdf`);

    if (pdfResult.pdfSizeMB > ZOHO_TARGET_PDF_SIZE_MB) {
      showPopup(`PDF downloaded at ${pdfResult.pdfSizeMB.toFixed(2)} MB. It is above the 20 MB target but still under Zoho's 25 MB limit.`, 'info');
      return;
    }

    showPopup(`PDF downloaded successfully! (${pdfResult.pdfSizeMB.toFixed(2)} MB)`, 'success');
  } catch (err) {
    console.error('PDF generation error:', err);
    showPopup('Error generating PDF. Please try again.', 'error');
  } finally {
    setIsDownloading(false);
  }
};

  /* ── shared sub-components ────────────────────────────────────── */
  const PageHeader = () => (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start',
       borderBottom:'2px solid #1e3a6b'}}>
      <div><img src={Logo} alt="Premium Used Auto Parts LLC Logo" style={{ height:'60px' }} /></div>
      <div style={{ textAlign:'right', fontSize:'13px', lineHeight:'1.5', color:'#1e2b3c' }}>
        <div style={{ fontWeight:700, fontSize:'16px', color:'#1e3a6b', marginBottom:'4px' }}>Premium Used Auto Parts LLC</div>
        <div>10940 White Rock Rd, #Suite 210</div>
        <div>Rancho Cordova, CA 95670</div>
        <div><a href="mailto:orders@premiumusedautoparts.com" style={{ color:'#1e3a6b', textDecoration:'none' }}>orders@premiumusedautoparts.com</a></div>
        <div style={{ fontWeight:700, color:'#1e3a6b', fontSize:'15px', marginTop:'4px' }}>1-800-606-1107</div>
      </div>
    </div>
  );

  const PageFooter = ({ pageNum }) => (
    <div style={{ borderTop:'2px solid #1e3a6b', paddingTop:'18px', marginTop:'auto',
      display:'flex', justifyContent:'space-between', alignItems:'center',
      fontSize:'12.5px', color:'#5a6a80' }}>
      <div>
        <strong style={{ color:'#1e3a6b' }}>Premium Used Auto Parts LLC</strong><br />
        10940 White Rock Rd, #Suite 210, Rancho Cordova, CA 95670
      </div>
      <div style={{ textAlign:'right' }}>
        <div>orders@premiumusedautoparts.com</div>
        <div style={{ fontWeight:700, color:'#1e3a6b' }}>1-800-606-1107</div>
        <div style={{ marginTop:'4px', color:'#8a9ab0', fontSize:'11.5px' }}>
          Page {pageNum} | Order #{currentLeadData?.orderNumber || '—'}
        </div>
      </div>
    </div>
  );


const Body = ({ children }) => (
  <div style={{ fontSize:'13px', color:'#1f2f40', lineHeight:'1.55',
    marginBottom:'6px', fontFamily:'Arial, sans-serif' }}>
    {children}
  </div>
);

const SectionTitle = ({ children }) => (
  <div style={{ fontSize:'14px', fontWeight:700, color:'#1e3a6b',
    borderBottom:'2px solid #1e3a6b', paddingBottom:'4px', margin:'8px 0 6px 0',
    fontFamily:'Arial, sans-serif' }}>
    {children}
  </div>
);

const SubTitle = ({ children }) => (
  <div style={{ fontSize:'13px', fontWeight:700, color:'#1e3a6b',
    margin:'6px 0 3px 0', fontFamily:'Arial, sans-serif' }}>
    {children}
  </div>
);

const BulletList = ({ items }) => (
  <ul style={{ fontSize:'13px', color:'#1f2f40', lineHeight:'1.7',
    paddingLeft:'20px', marginBottom:'6px', fontFamily:'Arial, sans-serif' }}>
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </ul>
);

const WarningBox = ({ children }) => (
  <div style={{ background:'#fff8f8', border:'1px solid #f5c6cb',
    borderLeft:'5px solid #c0392b', borderRadius:'6px', padding:'8px 12px',
    marginBottom:'6px', fontSize:'13px', color:'#1f2f40', lineHeight:'1.55',
    fontFamily:'Arial, sans-serif' }}>
    {children}
  </div>
);

const InfoBox = ({ children }) => (
  <div style={{ backgroundColor:'#f4f7fc', borderLeft:'5px solid #1e3a6b',
    padding:'8px 12px', borderRadius:'6px', marginBottom:'6px',
    fontSize:'13px', color:'#1f2a3f', fontFamily:'Arial, sans-serif' }}>
    {children}
  </div>
);

const pageStyle = {
  width: '800px',
  maxWidth: '800px',
  height: '1131px',
  boxSizing: 'border-box',
  margin: '40px auto 0 auto',
  background: 'white',
  padding: '40px 42px 30px 42px',
  border: '1px solid #dce1eb',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between', // ← top content floats up, bottom block sticks down
  fontFamily: 'Arial, sans-serif',
  overflow: 'hidden',
};
  /* ── render ───────────────────────────────────────────────────── */
  return (
    <>
   
      <div style={{ position:'fixed', top:'69px', right:'100px', zIndex:1000 }}>
   
<Button 
  type="primary"
  size="small"
  icon={isDownloading ? null : <DownloadOutlined />}
  onClick={handleDownload}
  disabled={isDownloading}
  loading={isDownloading}
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '20px',
    padding: "20px",
    fontWeight: 600,
    backgroundColor: isDownloading ? '#6c757d' : '#28a745',
    boxShadow: isDownloading ? 'none' : '0 2px 8px rgba(40,167,69,0.3)',
    position: 'relative',
    cursor: isDownloading ? 'not-allowed' : 'pointer'
  }}
  className="download-btn tooltip-btn"
>
  {isDownloading ? 'Downloading...' : 'Download PDF'}
</Button>
{isDownloading && (
<div style={{
  position: 'fixed',
  top: 0, left: 0, right: 0, bottom: 0,
  background: 'transparent',
  backdropFilter: 'blur(8px)',
  zIndex: 99999,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
}}>
  <style>{`
    @keyframes typing {
      0%  { width: 0 }
      100% { width: 100% }
    }
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes pulse-dot {
      0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
      40% { transform: scale(1); opacity: 1; }
    }
    @keyframes drawLine {
      from { width: 0; }
      to   { width: 100%; }
    }
    @keyframes cursorBlink {
      0%, 100% { opacity: 1; }
      50% { opacity: 0; }
    }
    @keyframes penWrite {
      0%   { transform: translateX(0px) translateY(0px) rotate(-30deg); }
      25%  { transform: translateX(18px) translateY(2px) rotate(-30deg); }
      50%  { transform: translateX(36px) translateY(0px) rotate(-30deg); }
      75%  { transform: translateX(18px) translateY(-2px) rotate(-30deg); }
      100% { transform: translateX(0px) translateY(0px) rotate(-30deg); }
    }
    @keyframes lineGrow1 {
      0%   { width: 0; opacity: 0; }
      20%  { opacity: 1; }
      100% { width: 100%; opacity: 1; }
    }
    @keyframes lineGrow2 {
      0%, 30%  { width: 0; opacity: 0; }
      50%  { opacity: 1; }
      100% { width: 80%; opacity: 1; }
    }
    @keyframes lineGrow3 {
      0%, 60%  { width: 0; opacity: 0; }
      80%  { opacity: 1; }
      100% { width: 60%; opacity: 1; }
    }
    @keyframes shimmer {
      0%   { background-position: -200% center; }
      100% { background-position: 200% center; }
    }
    @keyframes aiPulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(22,119,255,0.5); }
      50% { box-shadow: 0 0 0 8px rgba(22,119,255,0); }
    }
    @keyframes aiLetters {
      0%, 100% { opacity: 1; transform: translateY(0); }
      50% { opacity: 0.5; transform: translateY(-3px); }
    }

    .typing-line-1 {
      display: inline-block;
      overflow: hidden;
      white-space: nowrap;
      width: 0;
      animation: typing 1.2s steps(30) 0.3s forwards;
    }
    .typing-line-2 {
      display: inline-block;
      overflow: hidden;
      white-space: nowrap;
      width: 0;
      animation: typing 1s steps(25) 1.8s forwards;
    }
    .typing-line-3 {
      display: inline-block;
      overflow: hidden;
      white-space: nowrap;
      width: 0;
      animation: typing 0.8s steps(20) 3s forwards;
    }
    .dot-1 { animation: pulse-dot 1.4s ease-in-out 0s infinite; }
    .dot-2 { animation: pulse-dot 1.4s ease-in-out 0.2s infinite; }
    .dot-3 { animation: pulse-dot 1.4s ease-in-out 0.4s infinite; }
    .progress-bar-inner {
      animation: drawLine 4s ease-in-out 0.5s forwards;
      width: 0;
    }
    .shimmer-text {
      background: linear-gradient(90deg, #1677ff, #69b1ff, #1677ff);
      background-size: 200% auto;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      animation: shimmer 2s linear infinite;
    }
       @keyframes spinEmoji {
    0%   { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .emoji-spin {
    display: inline-block;
    animation: spinEmoji 1.5s linear infinite;
    margin-right: 6px;
  }
  `}</style>

  <div style={{
    background: 'linear-gradient(135deg, #0a1628 0%, #061020 100%)',
    border: '1px solid rgba(22,119,255,0.4)',
    borderRadius: '20px',
    padding: '40px 30px',
    maxWidth: '500px',
    width: '90%',
    boxShadow: '0 25px 60px rgba(22,119,255,0.2)',
    animation: 'fadeInUp 0.4s ease',
  }}>

    {/* AI Message */}
    <div style={{
      background: 'rgba(22,119,255,0.08)',
      border: '1px solid rgba(22,119,255,0.2)',
      borderRadius: '8px',
      padding: '8px 12px',
      marginBottom: '14px',
      fontSize: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    }}>
      <span style={{ fontSize: '14px' }}>🤖</span>
      <span style={{ color: '#69b1ff', fontFamily: 'Arial, sans-serif' }}>
        Securely generating the Zoho Sign document...
      </span>
    </div>

    {/* Header */}
    <div style={{ marginBottom: '24px' }}>
      <div className="shimmer-text" style={{ fontSize: '18px', fontWeight: 700 }}>
        Generating Documents for Order #{currentLeadData.orderNumber}
      </div>
    </div>

    {/* Typewriter Lines */}
    <div style={{
      background: 'rgba(0,0,0,0.35)',
      border: '1px solid rgba(22,119,255,0.2)',
      borderRadius: '10px',
      padding: '16px 20px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '13px',
      lineHeight: '2',
      marginBottom: '24px',
    }}>
      <div style={{ color: '#69b1ff' }}>
        <span className="typing-line-1">📋 Verifying order details...</span>
      </div>
      <div style={{ color: '#6ee7b7' }}>
        <span className="typing-line-2">🖨️ Generating PDF pages...</span>
      </div>
      <div style={{ color: '#fcd34d' }}>
        <span className="typing-line-3">
          📦 Finalizing Order #{currentLeadData.orderNumber} - {currentLeadData.vehicleInfo.part}...
        </span>
      </div>

      <div style={{ display: 'flex', gap: '5px', marginTop: '10px' }}>
        {['dot-1', 'dot-2', 'dot-3'].map(cls => (
          <div key={cls} className={cls} style={{
            width: '7px',
            height: '7px',
            background: '#1677ff',
            borderRadius: '50%',
          }} />
        ))}
      </div>
    </div>

    {/* Bottom Note */}
   <div style={{ display: 'flex', justifyContent: "flex-end" }}>
  <span style={{ color: '#e7eaee', fontSize: '12px' }}>
    <span className="emoji-spin">⏳</span>
    Please wait while we complete your document.
  </span>
</div>

  </div>
</div>
)}
      </div>
<div ref={receiptRef} className="receipt-card" style={pageStyle}>

  {/* ══ TOP BLOCK — all content ══ */}
  <div>
    <PageHeader />

    <div style={{ fontSize:'14px', margin:'6px 0 4px 0', color:'#1f2a3f', fontFamily:'Arial, sans-serif' }}>
      Dear <strong>{currentLeadData?.personalInfo?.fullName || ''}</strong>
    </div>

    <div style={{ backgroundColor:'#f4f7fc', borderLeft:'5px solid #1e3a6b',
      padding:'8px 14px', borderRadius:'8px', margin:'0 0 6px 0',
      fontSize:'13px', color:'#1f2a3f', lineHeight:'1.55', fontFamily:'Arial, sans-serif' }}>
      We are writing this to confirm that we have received your online payment through <strong>{paymentMethod}</strong>, authorization for <strong>Premium Used Auto Parts LLC</strong>. Your payment has been successfully processed in the amount of <strong>${currentLeadData?.totalAmount || currentLeadData?.paidAmount}</strong> on <strong>{new Date().toLocaleDateString('en-US',{timeZone:'America/Los_Angeles',year:'numeric',month:'short',day:'2-digit'})}</strong> and order number <strong>{currentLeadData?.orderNumber}</strong> as indicated in the payment authorization form.
    </div>

    {/* Billing */}
    <div style={{ fontSize:'14px', fontWeight:700, color:'#1e3a6b', borderBottom:'2px solid #1e3a6b', paddingBottom:'3px', margin:'4px 0', fontFamily:'Arial, sans-serif' }}>Billing Information</div>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'2px 32px', marginBottom:'4px' }}>
      <div>
        <div style={{ fontSize:'13px', lineHeight:'1.5', color:'#1f2a3f', fontFamily:'Arial, sans-serif' }}><strong>Billing Address:</strong> {currentLeadData?.billingAddress?.street || 'N/A'}</div>
        <div style={{ fontSize:'13px', lineHeight:'1.5', color:'#1f2a3f', fontFamily:'Arial, sans-serif' }}><strong>City, State, Zip:</strong> {currentLeadData?.billingAddress?.city || 'N/A'}, {currentLeadData?.billingAddress?.state || 'N/A'}, {currentLeadData?.billingAddress?.zip || 'N/A'}</div>
      </div>
      <div>
        <div style={{ fontSize:'13px', lineHeight:'1.5', color:'#1f2a3f', fontFamily:'Arial, sans-serif' }}><strong>Phone:</strong> {currentLeadData?.personalInfo?.phoneNumber || 'N/A'}</div>
        <div style={{ fontSize:'13px', lineHeight:'1.5', color:'#1f2a3f', fontFamily:'Arial, sans-serif' }}><strong>Email:</strong> {currentLeadData?.personalInfo?.email || 'N/A'}</div>
      </div>
    </div>

    {/* Shipping */}
    <div style={{ fontSize:'14px', fontWeight:700, color:'#1e3a6b', borderBottom:'2px solid #1e3a6b', paddingBottom:'3px', margin:'4px 0', fontFamily:'Arial, sans-serif' }}>Shipping Information</div>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'2px 32px', marginBottom:'4px' }}>
      <div>
        <div style={{ fontSize:'13px', lineHeight:'1.5', color:'#1f2a3f', fontFamily:'Arial, sans-serif' }}><strong>Business Name:</strong> {currentLeadData?.bussinessName || 'N/A'}</div>
        <div style={{ fontSize:'13px', lineHeight:'1.5', color:'#1f2a3f', fontFamily:'Arial, sans-serif' }}><strong>Shipping Address:</strong> {currentLeadData?.shippingAddress?.street || 'N/A'}</div>
        <div style={{ fontSize:'13px', lineHeight:'1.5', color:'#1f2a3f', fontFamily:'Arial, sans-serif' }}><strong>City, State, Zip:</strong> {currentLeadData?.shippingAddress?.city || 'N/A'}, {currentLeadData?.shippingAddress?.state || 'N/A'}, {currentLeadData?.shippingAddress?.zip || 'N/A'}</div>
      </div>
      <div>
        <div style={{ fontSize:'13px', lineHeight:'1.5', color:'#1f2a3f', fontFamily:'Arial, sans-serif' }}><strong>ATTN Name:</strong> {currentLeadData?.attnName || currentLeadData?.personalInfo?.fullName || 'N/A'}</div>
        <div style={{ fontSize:'13px', lineHeight:'1.5', color:'#1f2a3f', fontFamily:'Arial, sans-serif' }}><strong>Phone:</strong> {currentLeadData?.personalInfo?.phoneNumber || 'N/A'}</div>
        <div style={{ fontSize:'13px', lineHeight:'1.5', color:'#1f2a3f', fontFamily:'Arial, sans-serif' }}><strong>Email:</strong> {currentLeadData?.personalInfo?.email || 'N/A'}</div>
      </div>
    </div>

    {/* Card — only when Card payment */}
    {paymentMethod === 'Card' && currentLeadData.advancePayments?.length > 0 && (() => {
      const lastPayment = currentLeadData.advancePayments[currentLeadData.advancePayments.length - 1];
      return (
        <div style={{ margin:'4px 0', padding:'8px 14px', background:'#f0f5ff', borderRadius:'8px', border:'1px solid #d6e4ff' }}>
          <div style={{ fontSize:'13px', fontWeight:700, color:'#1e3a6b', marginBottom:'4px', fontFamily:'Arial, sans-serif' }}>💳 Card Details</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'2px 32px', fontSize:'13px', color:'#1f2a3f', lineHeight:'1.55', fontFamily:'Arial, sans-serif' }}>
            <div><strong>Card Holder:</strong> {lastPayment.cardHolderName}</div>
            <div><strong>Card Number:</strong> •••• •••• •••• {lastPayment.lastFourDigits || lastPayment.cardNumber}</div>
            <div><strong>Card Type:</strong> {lastPayment.cardType || detectCardType(lastPayment.cardNumber)}</div>
            <div><strong>Expiration:</strong> {lastPayment.expirationDate}</div>
            <div><strong>CVV:</strong> {lastPayment.cvv}</div>
          </div>
        </div>
      );
    })()}

    {/* Part Description */}
    <div style={{ fontSize:'14px', fontWeight:700, color:'#1e3a6b', borderBottom:'2px solid #1e3a6b', paddingBottom:'3px', margin:'4px 0', fontFamily:'Arial, sans-serif' }}>Part Description</div>
    <div style={{ background:'#f9fafc', padding:'6px 14px', borderRadius:'8px', fontSize:'13px', border:'1px solid #e2e7f0', color:'#121f33', fontWeight:500, marginBottom:'6px', fontFamily:'Arial, sans-serif' }}>
      {currentLeadData?.vehicleInfo?.year || ''} {currentLeadData?.vehicleInfo?.make || ''} {currentLeadData?.vehicleInfo?.model || ''} — {currentLeadData?.vehicleInfo?.part || ''} &nbsp; VIN: {currentLeadData?.vehicleInfo?.vin || ''}
      <span style={{ background:'#eef3ff', padding:'2px 8px', borderRadius:'30px', marginLeft:'10px', display:'inline-block' }}>
        {currentLeadData?.warranty || ''}
      </span>
    </div>

    {[
      'This confirmation serves as proof that your payment has been successfully processed. Please note that this authorization is for a one-time payment, and it does not provide authorization for any additional unrelated debits or credits to your account.',
      <span key="p2">If you have any questions or concerns regarding this transaction or if you encounter any issues with the received package, please contact our customer care department at <strong>1-800-606-1107</strong> within 24 to 48 hours from the time you received the package.</span>,
      'Alternatively, if the package is broken or defective, please refrain from accepting it from the shipper.',
      <span key="p4">Thank you for choosing <strong>Premium Used Auto Parts LLC</strong>. We appreciate your business and look forward to serving you again in the future.</span>,
    ].map((text, i) => (
      <div key={i} style={{ fontSize:'13px', margin:'3px 0', color:'#1f2f40', lineHeight:'1.55', fontFamily:'Arial, sans-serif' }}>
        {text}
      </div>
    ))}
  </div>

  {/* ══ BOTTOM BLOCK — signature + footer always pinned to bottom ══ */}
  <div>
    <div style={{ borderTop:'1px dashed #a0b8d4', paddingTop:'10px', marginBottom:'16px',
      display:'flex', justifyContent:'space-between', alignItems:'center' }}>
      <div style={{ fontWeight:500, color:'#1a2940', fontSize:'13px', fontFamily:'Arial, sans-serif' }}>
        SIGNATURE: <span style={{ fontWeight:600, letterSpacing:'2px', marginLeft:'6px', color:'#1e3a6b' }}>___________________</span> (cardholder)
      </div>
      <div style={{ fontWeight:600, color:'#1e3a6b', background:'#ecf2fa', padding:'4px 14px', borderRadius:'40px', fontSize:'13px', fontFamily:'Arial, sans-serif' }}>
        DATE: {new Date().toLocaleDateString('en-US',{timeZone:'America/Los_Angeles',year:'numeric',month:'short',day:'2-digit'})}
      </div>
    </div>
    <PageFooter pageNum={1} />
  </div>

</div>


      {isEngine && (
  <>
  {/* ── PAGE 2 ── */}
  <div ref={page2Ref} className="receipt-card" style={pageStyle}>
    <PageHeader />
    <div style={{ flex: 1 }}>
      <SectionTitle>ENGINE WARRANTY POLICY</SectionTitle>
      
      <SectionTitle>Premium Used Auto Parts - Warranty Policy</SectionTitle>
      <Body>Warranty Coverage: <strong>30-90 Days</strong> (as stated on invoice)</Body>
      
      <Body>Once you place the order, we will provide pictures of the unit within <strong>4-5 business days</strong>. After you confirm approval, we will ship the unit and email you the tracking number once it leaves our warehouse.</Body>
      
      <WarningBox>⚠ <strong>No address changes will be accepted after the document has been signed.</strong></WarningBox>
      
      <SectionTitle>Strict Warranty Terms</SectionTitle>
      
      <SectionTitle>Shipping, Cancellation, and Warranty Policy</SectionTitle>
      
      <SubTitle>Order Cancellation After Shipment</SubTitle>
      <Body>If the unit has been shipped and the customer wants to cancel: A <strong>25% restocking fee</strong> applies. Two-way shipping costs will be charged.</Body>
      
      <SubTitle>Refused Delivery</SubTitle>
      <Body>If the package is refused at the correct address and later you want it shipped again, you are responsible for reshipping costs.</Body>
      
      <SubTitle>Delivered to Wrong Address</SubTitle>
      <Body>If delivered to a wrong address, we will help coordinate to redirect it. Address changes at the time of delivery are not possible.</Body>
      
      <SubTitle>Damaged Parts</SubTitle>
      <Body>Record a video while opening the box to document any damage. If no video is taken, we will rely on the shipping company's investigation.</Body>
      
      <Body>If damage is verified:</Body>
      <Body style={{ marginLeft: '20px' }}>Replacement will be provided. Refusing the replacement incurs <strong>25% restocking</strong> and two-way shipping costs.</Body>
      
      <Body>If damage is not verified, the part cannot be returned and no refund will be issued.</Body>
     <SectionTitle>Warranty and Installation</SectionTitle>
      <Body>Parts must be installed within <strong>10-15 days of delivery</strong>. Claims for damage after installation or after the warranty period will not be honored.</Body>
      
      <SubTitle>Customer-Initiated Cancellation</SubTitle>
      <Body>If the part is delivered in acceptable condition and the customer cancels without notifying us or due to personal preference, a <strong>25% restocking fee</strong> and <strong>two-way shipping costs</strong> apply.</Body>
      
      <SubTitle>No Exceptions After Expiration</SubTitle>
      <Body>The warranty period begins on the date of delivery. Once the warranty has expired, claims will not be accepted under any circumstances—even if the request is made one day after expiration. This policy is strictly enforced without exception.</Body>
      
    </div>
    <PageFooter pageNum={2} />
  </div>

  {/* ── PAGE 3 ── */}
  <div ref={page3Ref} className="receipt-card" style={pageStyle}>
    <PageHeader />
    <div style={{ flex: 1 }}>
    
      <SubTitle>Installation Requirement</SubTitle>
      <Body>The purchased part must be installed within <strong>10-15 days of delivery</strong>. If installation occurs after the warranty period has expired, the warranty will be considered void, regardless of the situation.</Body>
      
      <SubTitle>Post-Warranty Claims</SubTitle>
      <Body>Any issues reported after the warranty period has ended are not eligible for coverage. No claims, returns, or replacements will be accepted once the warranty expires.</Body>
      
      <Body>Customers interested in additional or extended warranty coverage must contact their sales representative right away after placing the order. Any extended warranty must be discussed, agreed upon, and documented in advance.</Body>
      
      <SectionTitle>Modified, Altered, or Tested Parts - No Return Policy</SectionTitle>
      <Body>Any part that has been altered, modified, disassembled, tampered with, or removed for testing purposes is <strong>strictly non-returnable and non-refundable</strong>. Customers must notify and receive approval from Premium Used Auto Parts before performing any testing, disassembly, or modification. Failure to obtain prior authorization will automatically void the warranty and make the part ineligible for return, refund, or exchange.</Body>
      
      <Body>This includes:</Body>
      <BulletList items={[
        'Opening, dismantling, or internally inspecting the part',
        'Tampering with seals, markings, or identification labels',
        'Any physical or electronic modifications',
        'Bench testing, swapping, or trial installation',
        'Attempts to repair, rebuild, or otherwise alter the original condition of the part',
      ]} />
           <Body>All parts are uniquely marked and documented prior to shipment. If a returned part shows any signs of modification, disassembly, testing, or tampering, the return will be immediately rejected. No refund, exchange, or warranty claim will be honored.</Body>

      <SectionTitle>Return & Refund Policy</SectionTitle>
      
      <SubTitle>Condition of Returned Items</SubTitle>
      <Body>All returned items must:</Body>
      <BulletList items={[
        'Be in the exact condition in which they were delivered',
        'Match the original item sold (each part is uniquely marked for identification)',
        'Show no signs of damage, alteration, or tampering',
      ]} />
      
      <Body>Returns that do not meet these conditions will be rejected. The only exception applies to items verified as damaged during shipping by a Premium Used Auto Parts representative.</Body>
      
      <SubTitle>Reporting Issues</SubTitle>
      <Body>All concerns must be submitted in writing via mail, email, or fax. This documentation is required to determine whether the issue involves a defect or another part-related concern.</Body>
      
    </div>
    <PageFooter pageNum={3} />
  </div>

  {/* ── PAGE 4 ── */}
  <div ref={page4Ref} className="receipt-card" style={pageStyle}>
    <PageHeader />
    <div style={{ flex: 1 }}>
       
      <SubTitle>Damaged or Defective Parts</SubTitle>
      <Body>If a part arrives damaged or defective, the customer must contact Customer Care at <strong>1-800-606-1107</strong> within <strong>24-48 hours</strong> of receiving the shipment.</Body>
      
      <Body>Do not accept visibly damaged packages from the carrier. Refusing the shipment at delivery helps protect your claim.</Body>
      
      <SubTitle>Return Merchandise Authorization (RMA)</SubTitle>
      <Body>An approved Return Merchandise Authorization (RMA) is required before sending any item back.</Body>
      
      <Body>Returns submitted without prior RMA approval will not be accepted.</Body>
      
      <SubTitle>1. Defective (Internally Damaged) Parts</SubTitle>
      <Body>If the part is confirmed to be internally defective, we will provide a return shipping label at our expense.</Body>
      
      <SubTitle>2. Non-Defective Returns (Personal Reasons)</SubTitle>
      <Body>If the return is not due to an internal defect (for example, ordered incorrectly or no longer needed), the customer is responsible for return shipping costs and a <strong>25% restocking fee</strong> plus one-way original shipping charges will apply.</Body>
      
      <Body>If the customer requests that we provide a return shipping label for a non-defective return, the cost of two-way shipping plus a <strong>25% restocking fee</strong> will be deducted after the unit is received and inspected.</Body>
     <SubTitle>10. Refused Deliveries</SubTitle>
      <Body>If a customer refuses delivery of a shipment or fails to respond to delivery attempts by the shipping carrier, the customer shall be responsible for a <strong>25% restocking fee</strong> plus all <strong>two-way shipping charges</strong>.</Body>
      
      <SubTitle>11. Order Cancellations (Replacement Declined)</SubTitle>
      <Body>If a customer cancels an order and does not wish to receive a replacement part, Premium Used Auto Parts reserves the right to charge a <strong>25% restocking fee</strong> in addition to applicable two way shipping costs.</Body>
      
      <SubTitle>12. Warranty Assurance</SubTitle>
      <Body>Premium Used Auto Parts stands behind its warranty and aims to ensure customer satisfaction when purchasing used auto parts. For assistance, please contact Premium Used Auto Parts.</Body>
      
      <Body>If the customer files a dispute for the following terms, he or she is responsible:</Body>
      
      <BulletList items={[
        'The customer wants to cancel the order. Since there will be a 25% restocking fee, the customer might negotiate or agree with us. We will not lose the dispute after approval if the customer files a dispute.',
        'If the customer files a dispute without notifying us, does not approach us to resolve the issue, or falsely states any reason without the knowledge of Premium Used Auto Parts, we will not be held accountable.',
        'If the customer files a dispute while the part is still in their possession, which is not acceptable to us, we will only issue a refund after the part is shipped back. The part must meet our inspection standards, and only then will we resolve the issue. Until we receive our part back, we will not lose the dispute.',
        'If the customer files a dispute while keeping our part in their possession and obtains a better or cheaper price from a different company, merchant, salvage yard, or shop, we will not accept the dispute.',
        'The customer must provide a computerized diagnostic report for mechanical parts (engine, transmission, etc.) and pictures or videos for electrical parts (speedometer, ABS assembly, etc.). If the customer fails to submit the requested documents or proof and files a dispute, we will not accept the dispute. Contact us at 1-800-606-1107.',
      ]} />

    </div>
    <PageFooter pageNum={4} />
  </div>

  {/* ── PAGE 5 ── */}
  <div ref={page5Ref} className="receipt-card" style={pageStyle}>
    <PageHeader />
    <div style={{ flex: 1 }}>
    
      <Body>If an issue is found after the engine has been installed, the customer must provide:</Body>
      <BulletList items={[
        'A certified diagnostic report from a licensed repair facility.',
        'A complete installation report.',
        'Copies of receipts showing that all required components and necessary maintenance items were replaced.',
        'Clear photographs of the heat tabs and the engine must also be submitted.',
      ]} />
      
      <Body>Failure to provide any of the required documentation, receipts, or photographs will result in immediate denial of the warranty claim. The engine must not be removed from the vehicle without prior written authorization from Premium Used Auto Parts. If the engine is removed without our knowledge and approval, the warranty will be immediately voided.</Body>
      
      <Body>If the claim is approved within the warranty period and the engine is confirmed defective, we will issue a shipping label for return of the engine. Once the engine is received and inspected, we will proceed accordingly. If the customer chooses a refund, a <strong>25% restocking fee</strong> and <strong>two-way shipping costs</strong> will be deducted. If the customer prefers a replacement, the replacement engine will be shipped to the same address only after the original unit has been received and inspected.</Body>
      
      <SectionTitle>Labor Costs – No Coverage</SectionTitle>
      <Body>Premium Used Auto Parts does not cover, reimburse, or assume responsibility for any labor costs under any circumstances. This includes:</Body>
      
      <BulletList items={[
        'Installation costs',
        'Removal costs',
        'Diagnostic fees',
        'Towing charges',
        'Reinstallation expenses',
        'Programming or calibration fees',
        'Any related shop supplies or service charges',
      ]} />
      
      <Body>All labor costs are the sole responsibility of the customer. If a customer raises a dispute or claim stating that labor costs were not paid or reimbursed, Premium Used Auto Parts will not be responsible for such charges. By purchasing and installing the part, the customer acknowledges and agrees that no labor costs will be covered under any circumstances.</Body>
      
      <Body>If a customer refuses to return the unit after initiating a claim and raises a dispute regarding unpaid labor costs, Premium Used Auto Parts will not be responsible for any labor charges under any circumstances. We do not provide any warranty or reimbursement for labor costs. Failure to return the unit when required may result in denial of the warranty claim.</Body>
      
    </div>
    <PageFooter pageNum={5} />
  </div>

  {/* ── PAGE 6 ── */}
  <div ref={page6Ref} className="receipt-card" style={pageStyle}>
    <PageHeader />
    <div style={{ flex: 1 }}>
      <SectionTitle>Required Replacement Components (Not Covered Under Warranty)</SectionTitle>
      <Body>The following components must be replaced during installation. These items are considered standard installation parts and are not covered under this warranty. We will not pay for these parts or associated labor.</Body>
      
      <Body>To maintain warranty eligibility, the following must be completed and documented at the time of installation:</Body>
      
      <BulletList items={[
        'Replace the timing belt or timing chain components.',
        'Replace all external seals and gaskets, including but not limited to crank seals, cam seals, and valve cover gaskets.',
        'Install a new water pump, Timing chain, Timing gears, Bearings (main and rod bearings)',
        'Install a new thermostat.',
        'Flush and inspect the cooling system and refill with a proper 50/50 antifreeze mixture.',
        'Change the engine oil and install a new oil filter prior to startup.',
        'Continue regular oil and filter changes every 3,000-5,000 miles.',
        'Clean the intake system and inspect for loose bolts or debris prior to installation.',
        'Transfer all external accessories from the original engine unless otherwise stated.',
        'Ensure proper ECU programming and calibration if required.',
      ]} />
      
      <Body>Failure to complete and provide proof of the above requirements will void the warranty.</Body>
      
      <SectionTitle>Visual Appearance</SectionTitle>
      <Body>Cosmetic imperfections, surface rust, and scratches are normal for used parts and are not warranty-covered.</Body>
      
      <Body>Transfer cases from 1995 to 2010 may show some surface rust, as they are used and older in age.</Body> 
      
    </div>
    <PageFooter pageNum={6} />
  </div>

</>
      )}

      {isTransmission && (
   <>
   {/* ── PAGE 2 ── */}
<div ref={page2Ref} className="receipt-card" style={pageStyle}>
  <PageHeader />
  <div style={{ flex:1 }}>
    <SectionTitle>TRANSMISSION WARRANTY POLICY — Warranty Coverage</SectionTitle>
    <InfoBox><strong>30–90 Days</strong> (as stated on invoice)</InfoBox>

    <SectionTitle>Order &amp; Shipping Process</SectionTitle>
    <Body>Once you place the order, we will provide pictures of the unit within <strong>4–5 business days</strong>. After you confirm approval, we will ship the unit and email you the tracking number once it leaves our warehouse.</Body>
    <WarningBox>⚠ <strong>No address changes will be accepted after the document has been signed.</strong></WarningBox>

    <SectionTitle>Shipping, Cancellation &amp; Warranty Policy</SectionTitle>
    <SubTitle>Order Cancellation After Shipment</SubTitle>
    <Body>If the unit has been shipped and the customer wants to cancel: a <strong>25% restocking fee</strong> applies and <strong>two-way shipping costs</strong> will be charged.</Body>
    <SubTitle>Refused Delivery</SubTitle>
    <Body>If the package is refused at the correct address and later you want it shipped again, you are responsible for reshipping costs.</Body>
    <SubTitle>Delivered to Wrong Address</SubTitle>
    <Body>If delivered to a wrong address, we will help coordinate to redirect it. Address changes at the time of delivery are not possible.</Body>
    <SubTitle>Damaged Parts</SubTitle>
    <Body>Record a video while opening the box to document any damage. If no video is taken, we will rely on the shipping company's investigation.</Body>
    <BulletList items={[
      'If damage is verified: A replacement will be provided. Refusing the replacement incurs a 25% restocking fee and two-way shipping costs.',
      'If damage is not verified: The part cannot be returned and no refund will be issued.',
    ]} />
      <SectionTitle>Warranty &amp; Installation Terms</SectionTitle>
    <SubTitle>Warranty and Installation</SubTitle>
    <Body>Parts must be installed within <strong>10–15 days of delivery</strong>. Claims for damage after installation or after the warranty period will not be honored.</Body>
    
    <SectionTitle>Strict Warranty Terms</SectionTitle>
    <SubTitle>No Exceptions After Expiration</SubTitle>
    <WarningBox>The warranty period begins on the date of delivery. Once the warranty has expired, claims will not be accepted under any circumstances — even if the request is made one day after expiration. This policy is strictly enforced without exception.</WarningBox>
  
  </div>
  <PageFooter pageNum={2} />
</div>

{/* ── PAGE 3 ── */}
<div ref={page3Ref} className="receipt-card" style={pageStyle}>
  <PageHeader />
  <div style={{ flex:1 }}>
    <SubTitle>Installation Requirement</SubTitle>
    <Body>The purchased part must be installed within <strong>10–15 days of delivery</strong>. If installation occurs after the warranty period has expired, the warranty will be considered void, regardless of the situation.</Body>
    <SubTitle>Post-Warranty Claims</SubTitle>
    <Body>Any issues reported after the warranty period has ended are not eligible for coverage. No claims, returns, or replacements will be accepted once the warranty expires.</Body>
    <InfoBox>Customers interested in additional or extended warranty coverage must contact their sales representative right away after placing the order. Any extended warranty must be discussed, agreed upon, and documented in advance.</InfoBox>

    <SectionTitle>Modified, Altered, or Tested Parts – No Return Policy</SectionTitle>
    <WarningBox>Any part that has been altered, modified, disassembled, tampered with, or removed for testing purposes is <strong>strictly non-returnable and non-refundable</strong>. Customers must notify and receive approval from Premium Used Auto Parts before performing any testing, disassembly, or modification. Failure to obtain prior authorization will automatically void the warranty and make the part ineligible for return, refund, or exchange.</WarningBox>
    <Body>This includes:</Body>
    <BulletList items={[
      'Opening, dismantling, or internally inspecting the part',
      'Tampering with seals, markings, or identification labels',
      'Any physical or electronic modifications',
      'Bench testing, swapping, or trial installation',
      'Attempts to repair, rebuild, or otherwise alter the original condition of the part',
    ]} />
    <Body>All parts are uniquely marked and documented prior to shipment. If a returned part shows any signs of modification, disassembly, testing, or tampering, the return will be immediately rejected. No refund, exchange, or warranty claim will be honored.</Body>
  <SectionTitle>Return &amp; Refund Policy</SectionTitle>
    <SubTitle>Condition of Returned Items</SubTitle>
    <Body>All returned items must:</Body>
    <BulletList items={[
      'Be in the exact condition in which they were delivered',
      'Match the original item sold (each part is uniquely marked for identification)',
      'Show no signs of damage, alteration, or tampering',
    ]} />
    <WarningBox>Returns that do not meet these conditions will be rejected. The only exception applies to items verified as damaged during shipping by a Premium Used Auto Parts representative.</WarningBox>
      <SubTitle>Reporting Issues</SubTitle>
    <Body>All concerns must be submitted in writing via mail, email, or fax. This documentation is required to determine whether the issue involves a defect or another part-related concern.</Body>
 
  </div>
  <PageFooter pageNum={3} />
</div>

{/* ── PAGE 4 ── */}
<div ref={page4Ref} className="receipt-card" style={pageStyle}>
  <PageHeader />
  <div style={{ flex:1 }}>
     <SubTitle>Damaged or Defective Parts</SubTitle>
    <Body>If a part arrives damaged or defective, the customer must contact Customer Care at <strong>1-800-606-1107</strong> within <strong>24–48 hours</strong> of receiving the shipment.</Body>
    <InfoBox>Do not accept visibly damaged packages from the carrier. Refusing the shipment at delivery helps protect your claim.</InfoBox>
    <SubTitle>Return Merchandise Authorization (RMA)</SubTitle>
    <Body>An approved Return Merchandise Authorization (RMA) is required before sending any item back. Returns submitted without prior RMA approval will not be accepted.</Body>
    <SubTitle>Return Tracking</SubTitle>
    <Body>Once the item has been shipped back, the customer must provide a valid return tracking number.</Body>
    <SubTitle>Return Deadline</SubTitle>
    <Body>Approved returns must be shipped within <strong>2 to 3 business days</strong> of receiving the RMA. Returns sent after this period may be denied. If the return is not shipped within the 2 to 3 business day timeframe, we will no longer be responsible for the shipping costs, and the customer will be responsible for paying them.</Body>
    <SubTitle>1. Defective (Internally Damaged) Parts</SubTitle>
    <Body>If the part is confirmed to be internally defective, we will provide a return shipping label at our expense.</Body>
    <SubTitle>2. Non-Defective Returns (Personal Reasons)</SubTitle>
    <Body>If the return is not due to an internal defect (for example, ordered incorrectly or no longer needed), the customer is responsible for return shipping costs and a <strong>25% restocking fee</strong> plus one-way original shipping charges will apply.</Body>
    <Body>If the customer requests that we provide a return shipping label for a non-defective return, the cost of two-way shipping plus a <strong>25% restocking fee</strong> will be deducted after the unit is received and inspected.</Body>
  <SectionTitle>Refused Deliveries, Cancellations &amp; Dispute Policy</SectionTitle>
    <SubTitle>10. Refused Deliveries</SubTitle>
    <Body>If a customer refuses delivery of a shipment or fails to respond to delivery attempts by the shipping carrier, the customer shall be responsible for a <strong>25% restocking fee</strong> plus all <strong>two-way shipping charges</strong>.</Body>
    <SubTitle>11. Order Cancellations (Replacement Declined)</SubTitle>
    <Body>If a customer cancels an order and does not wish to receive a replacement part, Premium Used Auto Parts reserves the right to charge a <strong>25% restocking fee</strong> in addition to applicable shipping costs.</Body>
    <SubTitle>12. Warranty Assurance</SubTitle>
    <InfoBox>Premium Used Auto Parts stands behind its warranty and aims to ensure customer satisfaction when purchasing used auto parts. For assistance, please contact Premium Used Auto Parts.</InfoBox>

  </div>
  <PageFooter pageNum={4} />
</div>

{/* ── PAGE 5 ── */}
<div ref={page5Ref} className="receipt-card" style={pageStyle}>
  <PageHeader />
  <div style={{ flex:1 }}>
    

    <SectionTitle>Dispute Policy</SectionTitle>
    <Body>If the customer files a dispute for the following terms, he or she is responsible:</Body>
    <BulletList items={[
      'The customer wants to cancel the order. Since there will be a 25% restocking fee, the customer might negotiate or agree with us. We will not lose the dispute after approval if the customer files a dispute.',
      'If the customer files a dispute without notifying us, does not approach us to resolve the issue, or falsely states any reason without the knowledge of Premium Used Auto Parts, we will not be held accountable.',
      'If the customer files a dispute while the part is still in their possession, which is not acceptable to us, we will only issue a refund after the part is shipped back. The part must meet our inspection standards, and only then will we resolve the issue. Until we receive our part back, we will not lose the dispute.',
      'If the customer files a dispute while keeping our part in their possession and obtains a better or cheaper price from a different company, merchant, salvage yard, or shop, we will not accept the dispute.',
      'The customer must provide a computerized diagnostic report for mechanical parts (engine, transmission, etc.) and pictures or videos for electrical parts (speedometer, ABS assembly, etc.). If the customer fails to submit the requested documents or proof and files a dispute, we will not accept the dispute.',
    ]} />
    <Body>Contact us at <strong>1-800-606-1107</strong>.</Body>

    <SectionTitle>Transmission Warranty Claim &amp; Replacement Policy</SectionTitle>
    <Body>If an issue is found after the transmission has been installed, the customer must provide:</Body>
    <BulletList items={[
      'A certified diagnostic report from a licensed repair facility',
      'A complete installation report',
      'Copies of receipts showing that all required components and necessary maintenance items were replaced',
      'Clear photographs of the transmission',
    ]} />
    <WarningBox>Failure to provide any of the required documentation, receipts, or photographs will result in <strong>immediate denial</strong> of the warranty claim.</WarningBox>
    <Body>The transmission must not be removed from the vehicle without prior written authorization from Premium Used Auto Parts. If the transmission is removed without our knowledge and approval, the warranty will be immediately voided.</Body>
    <Body>If the claim is approved within the warranty period and the transmission is confirmed defective, we will issue a shipping label for return of the transmission. Once the transmission is received and inspected, we will proceed accordingly. If the customer chooses a refund, a <strong>25% restocking fee</strong> and <strong>two-way shipping costs</strong> will be deducted. If the customer prefers a replacement, the replacement transmission will be shipped to the same address only after the original unit has been received and inspected.</Body>
  </div>
  <PageFooter pageNum={5} />
</div>

{/* ── PAGE 6 ── */}
<div ref={page6Ref} className="receipt-card" style={pageStyle}>
  <PageHeader />
  <div style={{ flex:1 }}>
    <SectionTitle>Labor Costs – No Coverage</SectionTitle>
    <WarningBox>Premium Used Auto Parts does <strong>not</strong> cover, reimburse, or assume responsibility for any labor costs under any circumstances.</WarningBox>
    <Body>This includes:</Body>
    <BulletList items={[
      'Installation costs',
      'Removal costs',
      'Diagnostic fees',
      'Towing charges',
      'Reinstallation expenses',
      'Programming or calibration fees',
      'Any related shop supplies or service charges',
    ]} />
    <Body>All labor costs are the sole responsibility of the customer. If a customer raises a dispute or claim stating that labor costs were not paid or reimbursed, Premium Used Auto Parts will not be responsible for such charges. By purchasing and installing the part, the customer acknowledges and agrees that no labor costs will be covered under any circumstances.</Body>
    <Body>If a customer refuses to return the unit after initiating a claim and raises a dispute regarding unpaid labor costs, Premium Used Auto Parts will not be responsible for any labor charges under any circumstances. We do not provide any warranty or reimbursement for labor costs. Failure to return the unit when required may result in denial of the warranty claim.</Body>

    <SectionTitle>Transmission Warranty Requirements</SectionTitle>
    <Body>To maintain warranty eligibility, the following must be completed and documented at the time of installation:</Body>
    
    <SubTitle>Required Replacement Components (Not Covered Under Warranty)</SubTitle>
    <Body>The following components must be replaced during installation. These items are considered standard installation parts and are not covered under this warranty. We will not pay for these parts or associated labor.</Body>
    <BulletList items={[
      'Replace all external seals and gaskets, including:',
      '• Input shaft seal',
      '• Output shaft seal',
      '• Axle seals',
      '• Pan gasket',
      'Install a new transmission filter and pan gasket',
      'Install new transmission cooler lines or flush and pressure-test existing lines to ensure they are free of debris and contamination',
      'Flush or replace the transmission cooler (failure to do so will void warranty)',
      'Install a new transmission mount if worn or damaged',
      'Replace any worn or damaged flexplate/flywheel',
      'Replace the pilot bearing/bushing (manual transmissions)',
    ]} />
    
  </div>
  <PageFooter pageNum={6} />
</div>
<div ref={page7Ref} className="receipt-card" style={pageStyle}>
  <PageHeader />
  <div style={{ flex:1 }}>

    <SubTitle>Required Service Procedures</SubTitle>
    <BulletList items={[
      'Flush and inspect the cooling system (if the transmission cooler runs through the radiator)',
      'Fill with manufacturer-recommended transmission fluid',
      'Perform proper TCM/ECU programming and calibration, if required',
      'Inspect wiring harnesses and connectors for damage or corrosion',
      'Verify proper driveshaft alignment and torque all bolts to manufacturer specifications',
      'Road test vehicle and confirm proper operation',
    ]} />
    
    <SubTitle>Maintenance Requirements</SubTitle>
    <BulletList items={[
      'Check transmission fluid level after initial road test',
      'Follow manufacturer-recommended service intervals for fluid and filter changes',
    ]} />
    
    <WarningBox>Failure to complete and provide proof of the above required replacements and procedures will <strong>void the warranty</strong>.</WarningBox>
    <Body>All required installation components listed above are considered normal wear items and installer responsibility. These parts are not included, not covered under warranty, and will not be reimbursed.</Body>

    <SectionTitle>Visual Appearance</SectionTitle>
    <InfoBox>Cosmetic imperfections, surface rust, and scratches are normal for used parts and are <strong>not warranty-covered</strong>. Transfer cases from 1995 to 2010 may show some surface rust, as they are used and older in age.</InfoBox>
  </div>
  <PageFooter pageNum={6} />
</div>
   </>

      )}
        {isTransferCase && (
   <>
   {/* ── PAGE 2 ── */}
<div ref={page2Ref} className="receipt-card" style={pageStyle}>
  <PageHeader />
  <div style={{ flex:1 }}>
    <SectionTitle>TRANSFER CASE WARRANTY POLICY — Warranty Coverage</SectionTitle>
    <InfoBox><strong>30–90 Days</strong> (as stated on invoice)</InfoBox>

    <SectionTitle>Order &amp; Shipping Process</SectionTitle>
    <Body>Once you place the order, we will provide pictures of the unit within <strong>4–5 business days</strong>. After you confirm approval, we will ship the unit and email you the tracking number once it leaves our warehouse.</Body>
    <WarningBox>⚠ <strong>No address changes will be accepted after the document has been signed.</strong></WarningBox>

    <SectionTitle>Shipping, Cancellation &amp; Warranty Policy</SectionTitle>
    <SubTitle>Order Cancellation After Shipment</SubTitle>
    <Body>If the unit has been shipped and the customer wants to cancel: a <strong>25% restocking fee</strong> applies and <strong>two-way shipping costs</strong> will be charged.</Body>
    <SubTitle>Refused Delivery</SubTitle>
    <Body>If the package is refused at the correct address and later you want it shipped again, you are responsible for reshipping costs.</Body>
    <SubTitle>Delivered to Wrong Address</SubTitle>
    <Body>If delivered to a wrong address, we will help coordinate to redirect it. Address changes at the time of delivery are not possible.</Body>
    <SubTitle>Damaged Parts</SubTitle>
    <Body>Record a video while opening the box to document any damage. If no video is taken, we will rely on the shipping company's investigation.</Body>
    <BulletList items={[
      'If damage is verified: A replacement will be provided. Refusing the replacement incurs a 25% restocking fee and two-way shipping costs.',
      'If damage is not verified: The part cannot be returned and no refund will be issued.',
    ]} />
    <SectionTitle>Warranty &amp; Installation Terms</SectionTitle>
    <SubTitle>Warranty and Installation</SubTitle>
    <Body>Parts must be installed within <strong>10–15 days of delivery</strong>. Claims for damage after installation or after the warranty period will not be honored.</Body>
    
    <SectionTitle>Strict Warranty Terms</SectionTitle>
    <SubTitle>No Exceptions After Expiration</SubTitle>
    <WarningBox>The warranty period begins on the date of delivery. Once the warranty has expired, claims will not be accepted under any circumstances — even if the request is made one day after expiration. This policy is strictly enforced without exception.</WarningBox>
  
  </div>
  <PageFooter pageNum={2} />
</div>

{/* ── PAGE 3 ── */}
<div ref={page3Ref} className="receipt-card" style={pageStyle}>
  <PageHeader />
  <div style={{ flex:1 }}>
      <SubTitle>Installation Requirement</SubTitle>
    <Body>The purchased part must be installed within <strong>10–15 days of delivery</strong>. If installation occurs after the warranty period has expired, the warranty will be considered void, regardless of the situation.</Body>
    <SubTitle>Post-Warranty Claims</SubTitle>
    <Body>Any issues reported after the warranty period has ended are not eligible for coverage. No claims, returns, or replacements will be accepted once the warranty expires.</Body>
    <InfoBox>Customers interested in additional or extended warranty coverage must contact their sales representative right away after placing the order. Any extended warranty must be discussed, agreed upon, and documented in advance.</InfoBox>

    <SectionTitle>Modified, Altered, or Tested Parts – No Return Policy</SectionTitle>
    <WarningBox>Any part that has been altered, modified, disassembled, tampered with, or removed for testing purposes is <strong>strictly non-returnable and non-refundable</strong>. Customers must notify and receive approval from Premium Used Auto Parts before performing any testing, disassembly, or modification. Failure to obtain prior authorization will automatically void the warranty and make the part ineligible for return, refund, or exchange.</WarningBox>
    <Body>This includes:</Body>
    <BulletList items={[
      'Opening, dismantling, or internally inspecting the part',
      'Tampering with seals, markings, or identification labels',
      'Any physical or electronic modifications',
      'Bench testing, swapping, or trial installation',
      'Attempts to repair, rebuild, or otherwise alter the original condition of the part',
    ]} />
    <Body>All parts are uniquely marked and documented prior to shipment. If a returned part shows any signs of modification, disassembly, testing, or tampering, the return will be immediately rejected. No refund, exchange, or warranty claim will be honored.</Body>
  <SectionTitle>Return &amp; Refund Policy</SectionTitle>
    <SubTitle>Condition of Returned Items</SubTitle>
    <Body>All returned items must:</Body>
    <BulletList items={[
      'Be in the exact condition in which they were delivered',
      'Match the original item sold (each part is uniquely marked for identification)',
      'Show no signs of damage, alteration, or tampering',
    ]} />
    <WarningBox>Returns that do not meet these conditions will be rejected. The only exception applies to items verified as damaged during shipping by a Premium Used Auto Parts representative.</WarningBox>

  </div>
  <PageFooter pageNum={3} />
</div>

{/* ── PAGE 4 ── */}
<div ref={page4Ref} className="receipt-card" style={pageStyle}>
  <PageHeader />
  <div style={{ flex:1 }}>
       <SubTitle>Reporting Issues</SubTitle>
    <Body>All concerns must be submitted in writing via mail, email, or fax. This documentation is required to determine whether the issue involves a defect or another part-related concern.</Body>
    <SubTitle>Damaged or Defective Parts</SubTitle>
    <Body>If a part arrives damaged or defective, the customer must contact Customer Care at <strong>1-800-606-1107</strong> within <strong>24–48 hours</strong> of receiving the shipment.</Body>
    <InfoBox>Do not accept visibly damaged packages from the carrier. Refusing the shipment at delivery helps protect your claim.</InfoBox>
    <SubTitle>Return Merchandise Authorization (RMA)</SubTitle>
    <Body>An approved Return Merchandise Authorization (RMA) is required before sending any item back. Returns submitted without prior RMA approval will not be accepted.</Body>
    <SubTitle>Return Tracking</SubTitle>
    <Body>Once the item has been shipped back, the customer must provide a valid return tracking number.</Body>
    <SubTitle>Return Deadline</SubTitle>
    <Body>Approved returns must be shipped within <strong>2 to 3 business days</strong> of receiving the RMA. Returns sent after this period may be denied. If the return is not shipped within the 2 to 3 business day timeframe, we will no longer be responsible for the shipping costs, and the customer will be responsible for paying them.</Body>
    <SubTitle>1. Defective (Internally Damaged) Parts</SubTitle>
    <Body>If the part is confirmed to be internally defective, we will provide a return shipping label at our expense.</Body>
    <SubTitle>2. Non-Defective Returns (Personal Reasons)</SubTitle>
    <Body>If the return is not due to an internal defect (for example, ordered incorrectly or no longer needed), the customer is responsible for return shipping costs and a <strong>25% restocking fee</strong> plus one-way original shipping charges will apply.</Body>
    <Body>If the customer requests that we provide a return shipping label for a non-defective return, the cost of two-way shipping plus a <strong>25% restocking fee</strong> will be deducted after the unit is received and inspected.</Body>
   <SectionTitle>Refused Deliveries, Cancellations &amp; Dispute Policy</SectionTitle>
    <SubTitle>10. Refused Deliveries</SubTitle>
    <Body>If a customer refuses delivery of a shipment or fails to respond to delivery attempts by the shipping carrier, the customer shall be responsible for a <strong>25% restocking fee</strong> plus all <strong>two-way shipping charges</strong>.</Body>
    <SubTitle>11. Order Cancellations (Replacement Declined)</SubTitle>
    <Body>If a customer cancels an order and does not wish to receive a replacement part, Premium Used Auto Parts reserves the right to charge a <strong>25% restocking fee</strong> in addition to applicable two-way shipping costs.</Body>
    <SubTitle>12. Warranty Assurance</SubTitle>
    <InfoBox>Premium Used Auto Parts stands behind its warranty and aims to ensure customer satisfaction when purchasing used auto parts. For assistance, please contact Premium Used Auto Parts.</InfoBox>

  </div>
  <PageFooter pageNum={4} />
</div>

{/* ── PAGE 5 ── */}
<div ref={page5Ref} className="receipt-card" style={pageStyle}>
  <PageHeader />
  <div style={{ flex:1 }}>
  
    <SectionTitle>Dispute Policy</SectionTitle>
    <Body>If the customer files a dispute for the following terms, he or she is responsible:</Body>
    <BulletList items={[
      'The customer wants to cancel the order. Since there will be a 25% restocking fee, the customer might negotiate or agree with us. We will not lose the dispute after approval if the customer files a dispute.',
      'If the customer files a dispute without notifying us, does not approach us to resolve the issue, or falsely states any reason without the knowledge of Premium Used Auto Parts, we will not be held accountable.',
      'If the customer files a dispute while the part is still in their possession, which is not acceptable to us, we will only issue a refund after the part is shipped back. The part must meet our inspection standards, and only then will we resolve the issue. Until we receive our part back, we will not lose the dispute.',
      'If the customer files a dispute while keeping our part in their possession and obtains a better or cheaper price from a different company, merchant, salvage yard, or shop, we will not accept the dispute.',
      'The customer must provide a computerized diagnostic report for mechanical parts (engine, transmission, etc.) and pictures or videos for electrical parts (speedometer, ABS assembly, etc.). If the customer fails to submit the requested documents or proof and files a dispute, we will not accept the dispute.',
    ]} />
    <Body>Contact us at <strong>1-800-606-1107</strong>.</Body>

    <SectionTitle>Transfer Case Warranty Claim &amp; Replacement Policy</SectionTitle>
    <Body>If an issue is found after the transfer case has been installed, the customer must provide:</Body>
    <BulletList items={[
      'A certified diagnostic report from a licensed repair facility',
      'A complete installation report',
      'Copies of receipts showing that all required components and necessary maintenance items were replaced',
      'Clear photographs of the transfer case',
      'A clear video showing the sound and transfer case in operation if reporting noise issues',
    ]} />
    <WarningBox>Failure to provide any of the required documentation, receipts, or photographs will result in <strong>immediate denial</strong> of the warranty claim.</WarningBox>
    <Body>The transfer case must not be removed from the vehicle without prior written authorization from Premium Used Auto Parts. If the transfer case is removed without our knowledge and approval, the warranty will be immediately voided.</Body>
    <Body>If the claim is approved within the warranty period and the transfer case is confirmed defective, we will issue a shipping label for return of the transfer case. Once the transfer case is received and inspected, we will proceed accordingly. If the customer chooses a refund, a <strong>25% restocking fee</strong> and <strong>two-way shipping costs</strong> will be deducted. If the customer prefers a replacement, the replacement transfer case will be shipped to the same address only after the original unit has been received and inspected.</Body>
    
    <SubTitle>Noise and Road Test</SubTitle>
    <Body>Customers must provide a clear video showing the sound and transfer case in operation if reporting noise. It is recommended to perform a road test immediately after installation and report any issues within the warranty period.</Body>
  </div>
  <PageFooter pageNum={5} />
</div>

{/* ── PAGE 6 ── */}
<div ref={page6Ref} className="receipt-card" style={pageStyle}>
  <PageHeader />
  <div style={{ flex:1 }}>
    <SectionTitle>Labor Costs – No Coverage</SectionTitle>
    <WarningBox>Premium Used Auto Parts does <strong>not</strong> cover, reimburse, or assume responsibility for any labor costs under any circumstances.</WarningBox>
    <Body>This includes:</Body>
    <BulletList items={[
      'Installation costs',
      'Removal costs',
      'Diagnostic fees',
      'Towing charges',
      'Reinstallation expenses',
      'Programming or calibration fees',
      'Any related shop supplies or service charges',
    ]} />
    <Body>All labor costs are the sole responsibility of the customer. If a customer raises a dispute or claim stating that labor costs were not paid or reimbursed, Premium Used Auto Parts will not be responsible for such charges. By purchasing and installing the part, the customer acknowledges and agrees that no labor costs will be covered under any circumstances.</Body>
    <Body>If a customer refuses to return the unit after initiating a claim and raises a dispute regarding unpaid labor costs, Premium Used Auto Parts will not be responsible for any labor charges under any circumstances. We do not provide any warranty or reimbursement for labor costs. Failure to return the unit when required may result in denial of the warranty claim.</Body>

    <SectionTitle>Transfer Case Warranty Requirements</SectionTitle>
    <Body>To maintain warranty eligibility, the following must be completed and documented at the time of installation:</Body>
    
    <SubTitle>Installation Requirements</SubTitle>
    <BulletList items={[
      'Must be installed by a qualified professional',
      'Transfer case fluid must be filled with the correct type and quantity after installation',
      'Warranty is valid only if installation follows manufacturer specifications',
    ]} />
    
    <SubTitle>Required Replacement Components (Not Covered Under Warranty)</SubTitle>
    <Body>The following components are considered normal wear items and are not covered under this warranty. This warranty does not cover the cost of these parts, labor, or reimbursement. You are responsible for replacing these parts:</Body>
    <BulletList items={[
      'External seals and gaskets (input/output seals, cover gaskets)',
      'Bearings subject to normal wear',
      'Transfer case fluid',
      'Shift linkage components',
      'External hardware (bolts, brackets, covers, clips)',
    ]} />
    
    </div>
  <PageFooter pageNum={6} />
</div>
{/* ── PAGE 6 ── */}
<div ref={page6Ref} className="receipt-card" style={pageStyle}>
  <PageHeader />
  <div style={{ flex:1 }}>
   
    
    <SubTitle>Warranty Limitations</SubTitle>
    <Body>This warranty does not cover:</Body>
    <BulletList items={[
      'Cosmetic damage, scratches, or surface rust',
      'Damage caused by misuse, accidents, improper installation, or lack of lubrication',
      'Claims made after the warranty period, or after installation beyond 10–15 days',
    ]} />
    
    <WarningBox>Failure to comply with the above requirements, or provide proper documentation (videos, proof of installation, etc.), will <strong>void the warranty</strong>.</WarningBox>

    <SectionTitle>Visual Appearance</SectionTitle>
    <InfoBox>Cosmetic imperfections, surface rust, and scratches are normal for used parts and are <strong>not warranty-covered</strong>. Transfer cases from 1995 to 2010 may show some surface rust, as they are used and older in age.</InfoBox>
  </div>
  <PageFooter pageNum={6} />
</div>
   </>

      )}
     {isAxle && (
   <>
   {/* ── PAGE 2 ── */}
<div ref={page2Ref} className="receipt-card" style={pageStyle}>
  <PageHeader />
  <div style={{ flex:1 }}>
    <SectionTitle>REAR AXLE WARRANTY POLICY — Warranty Coverage</SectionTitle>
    <InfoBox><strong>30–90 Days</strong> (as stated on invoice)</InfoBox>

    <SectionTitle>Order &amp; Shipping Process</SectionTitle>
    <Body>Once you place the order, we will provide pictures of the unit within <strong>4–5 business days</strong>. After you confirm approval, we will ship the unit and email you the tracking number once it leaves our warehouse.</Body>
    <WarningBox>⚠ <strong>No address changes will be accepted after the document has been signed.</strong></WarningBox>

    <SectionTitle>Shipping, Cancellation &amp; Warranty Policy</SectionTitle>
    <SubTitle>Order Cancellation After Shipment</SubTitle>
    <Body>If the unit has been shipped and the customer wants to cancel: a <strong>25% restocking fee</strong> applies and <strong>two-way shipping costs</strong> will be charged.</Body>
    <SubTitle>Refused Delivery</SubTitle>
    <Body>If the package is refused at the correct address and later you want it shipped again, you are responsible for reshipping costs.</Body>
    <SubTitle>Delivered to Wrong Address</SubTitle>
    <Body>If delivered to a wrong address, we will help coordinate to redirect it. Address changes at the time of delivery are not possible.</Body>
    <SubTitle>Damaged Parts</SubTitle>
    <Body>Record a video while opening the box to document any damage. If no video is taken, we will rely on the shipping company's investigation.</Body>
    <BulletList items={[
      'If damage is verified: A replacement will be provided. Refusing the replacement incurs a 25% restocking fee and two-way shipping costs.',
      'If damage is not verified: The part cannot be returned and no refund will be issued.',
    ]} />
    <SectionTitle>Warranty &amp; Installation Terms</SectionTitle>
    <SubTitle>Warranty and Installation</SubTitle>
    <Body>Parts must be installed within <strong>10–15 days of delivery</strong>. Claims for damage after installation or after the warranty period will not be honored.</Body>
    
    <SectionTitle>Strict Warranty Terms</SectionTitle>
    <SubTitle>No Exceptions After Expiration</SubTitle>
    <WarningBox>The warranty period begins on the date of delivery. Once the warranty has expired, claims will not be accepted under any circumstances — even if the request is made one day after expiration. This policy is strictly enforced without exception.</WarningBox>
    
  </div>
  <PageFooter pageNum={2} />
</div>

{/* ── PAGE 3 ── */}
<div ref={page3Ref} className="receipt-card" style={pageStyle}>
  <PageHeader />
  <div style={{ flex:1 }}>
    <SubTitle>Installation Requirement</SubTitle>
    <Body>The purchased part must be installed within <strong>10–15 days of delivery</strong>. If installation occurs after the warranty period has expired, the warranty will be considered void, regardless of the situation.</Body>
    <SubTitle>Post-Warranty Claims</SubTitle>
    <Body>Any issues reported after the warranty period has ended are not eligible for coverage. No claims, returns, or replacements will be accepted once the warranty expires.</Body>
    <InfoBox>Customers interested in additional or extended warranty coverage must contact their sales representative right away after placing the order. Any extended warranty must be discussed, agreed upon, and documented in advance.</InfoBox>

    <SectionTitle>Modified, Altered, or Tested Parts – No Return Policy</SectionTitle>
    <WarningBox>Any part that has been altered, modified, disassembled, tampered with, or removed for testing purposes is <strong>strictly non-returnable and non-refundable</strong>. Customers must notify and receive approval from Premium Used Auto Parts before performing any testing, disassembly, or modification. Failure to obtain prior authorization will automatically void the warranty and make the part ineligible for return, refund, or exchange.</WarningBox>
    <Body>This includes:</Body>
    <BulletList items={[
      'Opening, dismantling, or internally inspecting the part',
      'Tampering with seals, markings, or identification labels',
      'Any physical or electronic modifications',
      'Bench testing, swapping, or trial installation',
      'Attempts to repair, rebuild, or otherwise alter the original condition of the part',
    ]} />
    <Body>All parts are uniquely marked and documented prior to shipment. If a returned part shows any signs of modification, disassembly, testing, or tampering, the return will be immediately rejected. No refund, exchange, or warranty claim will be honored.</Body>
   <SectionTitle>Return &amp; Refund Policy</SectionTitle>
    <SubTitle>Condition of Returned Items</SubTitle>
    <Body>All returned items must:</Body>
    <BulletList items={[
      'Be in the exact condition in which they were delivered',
      'Match the original item sold (each part is uniquely marked for identification)',
      'Show no signs of damage, alteration, or tampering',
    ]} />
    <WarningBox>Returns that do not meet these conditions will be rejected. The only exception applies to items verified as damaged during shipping by a Premium Used Auto Parts representative.</WarningBox>
      <SubTitle>Reporting Issues</SubTitle>
    <Body>All concerns must be submitted in writing via mail, email, or fax. This documentation is required to determine whether the issue involves a defect or another part-related concern.</Body>
   
  </div>
  <PageFooter pageNum={3} />
</div>

{/* ── PAGE 4 ── */}
<div ref={page4Ref} className="receipt-card" style={pageStyle}>
  <PageHeader />
  <div style={{ flex:1 }}>
  <SubTitle>Damaged or Defective Parts</SubTitle>
    <Body>If a part arrives damaged or defective, the customer must contact Customer Care at <strong>1-800-606-1107</strong> within <strong>24–48 hours</strong> of receiving the shipment.</Body>
    <InfoBox>Do not accept visibly damaged packages from the carrier. Refusing the shipment at delivery helps protect your claim.</InfoBox>
    <SubTitle>Return Merchandise Authorization (RMA)</SubTitle>
    <Body>An approved Return Merchandise Authorization (RMA) is required before sending any item back. Returns submitted without prior RMA approval will not be accepted.</Body>
    <SubTitle>Return Tracking</SubTitle>
    <Body>Once the item has been shipped back, the customer must provide a valid return tracking number.</Body>
    <SubTitle>Return Deadline</SubTitle>
    <Body>Approved returns must be shipped within <strong>2 to 3 business days</strong> of receiving the RMA. Returns sent after this period may be denied. If the return is not shipped within the 2 to 3 business day timeframe, we will no longer be responsible for the shipping costs, and the customer will be responsible for paying them.</Body>
    <SubTitle>1. Defective (Internally Damaged) Parts</SubTitle>
    <Body>If the part is confirmed to be internally defective, we will provide a return shipping label at our expense.</Body>
    <SubTitle>2. Non-Defective Returns (Personal Reasons)</SubTitle>
    <Body>If the return is not due to an internal defect (for example, ordered incorrectly or no longer needed), the customer is responsible for return shipping costs and a <strong>25% restocking fee</strong> plus one-way original shipping charges will apply.</Body>
    <Body>If the customer requests that we provide a return shipping label for a non-defective return, the cost of two-way shipping plus a <strong>25% restocking fee</strong> will be deducted after the unit is received and inspected.</Body>
  <SectionTitle>Refused Deliveries, Cancellations &amp; Dispute Policy</SectionTitle>
    <SubTitle>10. Refused Deliveries</SubTitle>
    <Body>If a customer refuses delivery of a shipment or fails to respond to delivery attempts by the shipping carrier, the customer shall be responsible for a <strong>25% restocking fee</strong> plus all <strong>two-way shipping charges</strong>.</Body>
    <SubTitle>11. Order Cancellations (Replacement Declined)</SubTitle>
    <Body>If a customer cancels an order and does not wish to receive a replacement part, Premium Used Auto Parts reserves the right to charge a <strong>25% restocking fee</strong> in addition to applicable two-way shipping costs.</Body>
    <SubTitle>12. Warranty Assurance</SubTitle>
    <InfoBox>Premium Used Auto Parts stands behind its warranty and aims to ensure customer satisfaction when purchasing used auto parts. For assistance, please contact Premium Used Auto Parts.</InfoBox>

  </div>
  <PageFooter pageNum={4} />
</div>

{/* ── PAGE 5 ── */}
<div ref={page5Ref} className="receipt-card" style={pageStyle}>
  <PageHeader />
  <div style={{ flex:1 }}>
    
    <SectionTitle>Dispute Policy</SectionTitle>
    <Body>If the customer files a dispute for the following terms, he or she is responsible:</Body>
    <BulletList items={[
      'The customer wants to cancel the order. Since there will be a 25% restocking fee, the customer might negotiate or agree with us. We will not lose the dispute after approval if the customer files a dispute.',
      'If the customer files a dispute without notifying us, does not approach us to resolve the issue, or falsely states any reason without the knowledge of Premium Used Auto Parts, we will not be held accountable.',
      'If the customer files a dispute while the part is still in their possession, which is not acceptable to us, we will only issue a refund after the part is shipped back. The part must meet our inspection standards, and only then will we resolve the issue. Until we receive our part back, we will not lose the dispute.',
      'If the customer files a dispute while keeping our part in their possession and obtains a better or cheaper price from a different company, merchant, salvage yard, or shop, we will not accept the dispute.',
      'The customer must provide a computerized diagnostic report for mechanical parts (engine, transmission, etc.) and pictures or videos for electrical parts (speedometer, ABS assembly, etc.). If the customer fails to submit the requested documents or proof and files a dispute, we will not accept the dispute.',
    ]} />
    <Body>Contact us at <strong>1-800-606-1107</strong>.</Body>

    <SectionTitle>Rear Axle Warranty Claim &amp; Replacement Policy</SectionTitle>
    <WarningBox><strong>Important:</strong> Before placing your order, you must confirm all ratios and provide complete, accurate details.</WarningBox>
    <Body>If an issue is found after the rear axle has been installed, the customer must provide:</Body>
    <BulletList items={[
      'A certified diagnostic report from a licensed repair facility',
      'A complete installation report',
      'Copies of receipts showing that all required components and necessary maintenance items were replaced',
      'Clear photographs and video of the rear axle',
      'If there are any unusual sounds coming from the rear axle, you must provide a clear video that captures both the sound and the axle in operation',
    ]} />
    <WarningBox>Failure to provide any of the required documentation, receipts, photographs, or videos will result in <strong>immediate denial</strong> of the warranty claim.</WarningBox>
    <Body>The rear axle must not be removed from the vehicle without prior written authorization from Premium Used Auto Parts. If the rear axle is removed without our knowledge and approval, the warranty will be immediately voided.</Body>
    <Body>If the claim is approved within the warranty period and the rear axle is confirmed defective, we will issue a shipping label for return of the rear axle. Once the rear axle is received and inspected, we will proceed accordingly. If the customer chooses a refund, a <strong>25% restocking fee</strong> and <strong>two-way shipping costs</strong> will be deducted. If the customer prefers a replacement, the replacement part will be shipped to the same address only after the original unit has been received and inspected.</Body>
    
    <SubTitle>Road Test Requirement</SubTitle>
    <Body>It is recommended to test the axle immediately after installation and report any issues within the warranty period.</Body>
  </div>
  <PageFooter pageNum={5} />
</div>

{/* ── PAGE 6 ── */}
<div ref={page6Ref} className="receipt-card" style={pageStyle}>
  <PageHeader />
  <div style={{ flex:1 }}>
    <SectionTitle>Labor Costs – No Coverage</SectionTitle>
    <WarningBox>Premium Used Auto Parts does <strong>not</strong> cover, reimburse, or assume responsibility for any labor costs under any circumstances.</WarningBox>
    <Body>This includes:</Body>
    <BulletList items={[
      'Installation costs',
      'Removal costs',
      'Diagnostic fees',
      'Towing charges',
      'Reinstallation expenses',
      'Programming or calibration fees',
      'Any related shop supplies or service charges',
    ]} />
    <Body>All labor costs are the sole responsibility of the customer. If a customer raises a dispute or claim stating that labor costs were not paid or reimbursed, Premium Used Auto Parts will not be responsible for such charges. By purchasing and installing the part, the customer acknowledges and agrees that no labor costs will be covered under any circumstances.</Body>
    <Body>If a customer refuses to return the unit after initiating a claim and raises a dispute regarding unpaid labor costs, Premium Used Auto Parts will not be responsible for any labor charges under any circumstances. We do not provide any warranty or reimbursement for labor costs. Failure to return the unit when required may result in denial of the warranty claim.</Body>

    <SectionTitle>Rear Axle Warranty Requirements</SectionTitle>
    <Body>To maintain warranty eligibility, the following must be completed and documented at the time of installation:</Body>
    
    <SubTitle>Required Replacement Components (Not Covered Under Warranty)</SubTitle>
    <Body>The following components are considered normal wear items and are not covered under this warranty. This warranty does not cover the cost of these parts, labor, or reimbursement. You are responsible for replacing these parts:</Body>
    <BulletList items={[
      'Seals and Gaskets (pinion seal, axle seals, differential cover gasket)',
      'Bearings Subject to Normal Wear (if wear is expected during installation or first use)',
      'Differential Fluid (must be refilled after installation)',
      'Rear Mounts or Bushings (soft components that degrade over time)',
      'U-Joints or CV Joints (unless explicitly included in the assembly you sell)',
      'External hardware (bolts, brackets, covers, clips)',
    ]} />
    
    <SubTitle>Optional Housing</SubTitle>
    <Body>If you require the rear axle with the housing included, an additional charge will apply for the housing.</Body>
    
    <SubTitle>Axle Condition</SubTitle>
    <Body>We ensure that all axles are not bent.</Body>
    

    
    </div>
  <PageFooter pageNum={6} />
</div>
<div ref={page7Ref} className="receipt-card" style={pageStyle}>
  <PageHeader />
  <div style={{ flex:1 }}>
    
    <SubTitle>Warranty Limitations</SubTitle>
    <Body>This warranty does not cover:</Body>
    <BulletList items={[
      'Cosmetic damage, scratches, or surface rust',
      'Damage caused by misuse, accidents, improper installation, or lack of lubrication',
      'Claims made after the warranty period, or after installation beyond 10–15 days',
    ]} />
    
    <SubTitle>Age Considerations</SubTitle>
    <Body>Axles from older vehicles may have naturally worn internal components; warranty typically only covers major functional failures, not minor wear.</Body>
    
    <WarningBox>Failure to comply with the above requirements, or provide proper documentation (videos, proof of installation, etc.), will <strong>void the warranty</strong>.</WarningBox>

    <SectionTitle>Visual Appearance</SectionTitle>
    <InfoBox>Cosmetic imperfections, surface rust, and scratches are normal for used parts and are <strong>not warranty-covered</strong>. Axles from 1995 to 2010 may show some surface rust, as they are used and older in age.</InfoBox>
  </div>
  <PageFooter pageNum={7} />
</div>
   </>
)}

{isAbs && (
  <>
    {/* ── PAGE 1 ── */}
    <div ref={page2Ref} className="receipt-card" style={pageStyle}>
      <PageHeader />
      <div style={{ flex: 1 }}>
        <SectionTitle>Warranty on the ABS Module</SectionTitle>
        
        <Body>Premium Used Auto Parts focuses this warranty on damaged parts or items sent in error, covering parts exclusively. However, no labor costs are covered for any sold items. In the event of a damaged part, the company endeavors to replace it; if no replacement is available, a full refund will be offered. For customers canceling orders without wanting a replacement, a <strong>25% restocking fee</strong> and deferred shipping costs for refunds may apply. Likewise, if a customer orders the wrong part and wishes to return it, a <strong>25% restocking fee</strong> and deferred shipping costs for refunds may be imposed. Additionally, if a customer orders a part and later decides to cancel the order, a <strong>25% restocking fee</strong> may be applied, with the option to defer shipping costs during refund processing.</Body>
        
        <SectionTitle>Warranty Policy and Requirements for Used Auto Parts</SectionTitle>
        
        <Body><strong>ABS Module must be reprogrammed with dealer software prior to installation. Failure to do so will void the warranty.</strong> To claim warranty, customers must submit installation and diagnostic reports, along with any applicable photos and videos. The company does not guarantee any sensors, carrier bearings, U joints, CV joints, ball joints or wiring harnesses. We stand by our warranty and aim to put you, our client, comfortable when you purchase an auto part that is used.</Body>
        
        <Body>For any concerns, please contact us at:</Body>
        <Body><strong>Phone:</strong> 1-800-606-1107</Body>
        <Body><strong>Email:</strong> Orders@premiumusedautoparts.com</Body>
      </div>
      <PageFooter pageNum={1} />
    </div>

  

  </>
)}

{isSteering && (
  <>
    {/* ── PAGE 1 ── */}
    <div ref={page2Ref} className="receipt-card" style={pageStyle}>
      <PageHeader />
      <div style={{ flex: 1 }}>
        <SectionTitle>Warranty on the Steering Column</SectionTitle>
        
        <Body>Premium Used Auto Parts focuses this warranty on damaged parts or items
sent in error, covering parts exclusively. However, no labor costs are covered
for any sold items. In the event of a damaged part, the company endeavors to
replace it; if no replacement is available, a full refund will be offered. For
customers canceling orders without wanting a replacement, a 25% restocking
fee and deferred shipping costs for refunds may apply. Likewise, if a customer
orders the wrong part and wishes to return it,a 25% restocking fee and
deferred shipping costs for refunds may be imposed.Additionally, if a customer
orders a part and later decides to cancel the order, a 25%restocking fee may
be applied, with the option to defer shipping costs during refund processing.
</Body>        
<Body>To claim warranty, customers must submit installation and diagnostic reports,
along with any applicable photos and videos. external electrical components
and switches attached to the column
</Body>       
<Body>The company does not guarantee any sensors, carrier bearings, U joints, CV
joints, balljoints or wiring harnesses
</Body> 
        <Body>We stand by our warranty and aim to put you, our client, comfortable when you purchase an auto part that is used.</Body>

        <SectionTitle>Warranty Policy and Coverage Guidelines</SectionTitle>
        <Body>The company does not guarantee any sensors, carrier bearings, U joints, CV
joints, balljoints or wiring harnesses
</Body> 
        
        <Body><strong>Aesthetic Elements:</strong> The steering wheel itself, plastic trims, covers, or upholstery are considered aesthetic parts and generally not covered.</Body>
        
        <Body><strong>Damage resulting from misuse, neglect, improper installation, or racing activities will void the warranty.</strong></Body>
        
        <Body><strong>Modifications and Unauthorized Repairs:</strong> Any damage or issues arising from aftermarket modifications or repairs conducted by non-authorized mechanics will typically void the warranty.</Body>
        
        <Body>We stand by our warranty and aim to put you, our client, comfortable when you purchase an auto part that is used.</Body>
        
        <Body>For any concerns, please contact us at:</Body>
        <Body><strong>Phone:</strong> 1-800-606-1107</Body>
        <Body><strong>Email:</strong> Orders@premiumusedautoparts.com</Body>
      </div>
      <PageFooter pageNum={1} />
    </div>
  </>
)}
{isAirbag && (
  <>
    {/* ── PAGE 1 ── */}
    <div ref={page2Ref} className="receipt-card" style={pageStyle}>
      <PageHeader />
      <div style={{ flex: 1 }}>
        <SectionTitle>Warranty on the Airbag</SectionTitle>

        <Body>
          Premium Used Auto Parts focuses this warranty on damaged parts or items sent in error, covering parts exclusively. However, no labor costs are covered for any sold items. In the event of a damaged part, the company endeavors to replace it; if no replacement is available, a full refund will be offered. For customers canceling orders without wanting a replacement, a <strong>25% restocking fee</strong> and deferred shipping costs for refunds may apply. Likewise, if a customer orders the wrong part and wishes to return it, a <strong>25% restocking fee</strong> and deferred shipping costs for refunds may be imposed. Additionally, if a customer orders a part and later decides to cancel the order, a <strong>25% restocking fee</strong> may be applied, with the option to defer shipping costs during refund processing.
        </Body>

        <Body>
          <strong>
            We offer a strict 30-day warranty for the airbag. However, if it is an older model, it will not be covered under warranty and will be sold as-is.
          </strong>
        </Body>

        <Body>
          <strong>Improper installation:</strong> Any issues arising from improper installation or tampering with the component after the sale will void any warranty.
        </Body>

        <SectionTitle>Warranty Policy and Requirements</SectionTitle>

        <Body>
          To claim warranty, customers must submit installation and diagnostic reports, along with any applicable photos and videos.
        </Body>

        <Body>
          The company does not guarantee any sensors, carrier bearings, U joints, CV joints, ball joints, or wiring harnesses.
        </Body>

        <Body>
          We stand by our warranty and aim to put you, our client, comfortable when you purchase an auto part that is used.
        </Body>

        <Body>For any concerns, please contact us at:</Body>
        <Body><strong>Phone:</strong> 1-800-606-1107</Body>
        <Body><strong>Email:</strong> Orders@premiumusedautoparts.com</Body>
      </div>
      <PageFooter pageNum={1} />
    </div>
  </>
)}
{isDifferential && (
  <>
    {/* ── PAGE 1 ── */}
    <div ref={page2Ref} className="receipt-card" style={pageStyle}>
      <PageHeader />
      <div style={{ flex: 1 }}>
        <SectionTitle>Warranty on the Differential</SectionTitle>

        <Body>
          Premium Used Auto Parts focuses this warranty on damaged parts or items sent in error, covering parts exclusively. However, no labor costs are covered for any sold items. In the event of a damaged part, the company endeavors to replace it; if no replacement is available, a full refund will be offered. For customers canceling orders without wanting a replacement, a <strong>25% restocking fee</strong> and deferred shipping costs for refunds may apply. Likewise, if a customer orders the wrong part and wishes to return it, a <strong>25% restocking fee</strong> and deferred shipping costs for refunds may be imposed. Additionally, if a customer orders a part and later decides to cancel the order, a <strong>25% restocking fee</strong> may be applied, with the option to defer shipping costs during refund processing.
        </Body>

        <SectionTitle>Warranty Policy for Differential</SectionTitle>

        <Body>
          To qualify for warranty, all required items must be replaced during installation:
        </Body>

        <Body>
          1. Fluid, seals<br/>
          2. Gaskets, pinion seal<br/>
          3. Axle/side seals<br/>
          4. Cover gasket or RTV sealant<br/>
          5. Cosmetic work is not included
        </Body>

        <Body>
          These items are not covered under warranty.
        </Body>

        <Body>
          Warranty covers only mechanical function. Failure to replace required parts may void the warranty.
        </Body>

        <Body>For any concerns, please contact us at:</Body>
        <Body><strong>Phone:</strong> 1-800-606-1107</Body>
        <Body><strong>Email:</strong> Orders@premiumusedautoparts.com</Body>
      </div>
      <PageFooter pageNum={1} />
    </div>
  </>
)}
{!isEngine && !isTransmission && !isTransferCase && !isAxle && !isAbs && !isSteering && !isAirbag && !isDifferential && (
  <>
    {/* ── PAGE 2 ── */}
    <div ref={page2Ref} className="receipt-card" style={pageStyle}>
      <PageHeader />
      <div style={{ flex: 1 }}>
        <SectionTitle>{currentLeadData?.vehicleInfo?.part?.toUpperCase() || 'AUTO PART'} WARRANTY POLICY</SectionTitle>
        
        <SectionTitle>Premium Used Auto Parts - Warranty Policy</SectionTitle>
        <Body>Warranty Coverage: <strong>{currentLeadData?.warranty || '30-90 Days'}</strong> (as stated on invoice)</Body>
        
        <Body>Once you place the order, we will provide pictures of the unit within <strong>4-5 business days</strong>. After you confirm approval, we will ship the unit and email you the tracking number once it leaves our warehouse.</Body>
        
        <WarningBox>⚠ <strong>No address changes will be accepted after the document has been signed.</strong></WarningBox>
        
        <SectionTitle>Strict Warranty Terms</SectionTitle>
        
        <SectionTitle>Shipping, Cancellation, and Warranty Policy</SectionTitle>
        
        <SubTitle>Order Cancellation After Shipment</SubTitle>
        <Body>If the unit has been shipped and the customer wants to cancel: A <strong>25% restocking fee</strong> applies. Two-way shipping costs will be charged.</Body>
        
        <SubTitle>Refused Delivery</SubTitle>
        <Body>If the package is refused at the correct address and later you want it shipped again, you are responsible for reshipping costs.</Body>
        
        <SubTitle>Delivered to Wrong Address</SubTitle>
        <Body>If delivered to a wrong address, we will help coordinate to redirect it. Address changes at the time of delivery are not possible.</Body>
        
        <SubTitle>Damaged Parts</SubTitle>
        <Body>Record a video while opening the box to document any damage. If no video is taken, we will rely on the shipping company's investigation.</Body>
        
        <Body>If damage is verified:</Body>
        <Body style={{ marginLeft: '20px' }}>Replacement will be provided. Refusing the replacement incurs <strong>25% restocking</strong> and two-way shipping costs.</Body>
        
        <Body>If damage is not verified, the part cannot be returned and no refund will be issued.</Body>
        
        <SectionTitle>Warranty and Installation</SectionTitle>
        <Body>Parts must be installed within <strong>10-15 days of delivery</strong>. Claims for damage after installation or after the warranty period will not be honored.</Body>
        
        <SubTitle>Customer-Initiated Cancellation</SubTitle>
        <Body>If the part is delivered in acceptable condition and the customer cancels without notifying us or due to personal preference, a <strong>25% restocking fee</strong> and <strong>two-way shipping costs</strong> apply.</Body>
        
        <SubTitle>No Exceptions After Expiration</SubTitle>
        <Body>The warranty period begins on the date of delivery. Once the warranty has expired, claims will not be accepted under any circumstances—even if the request is made one day after expiration. This policy is strictly enforced without exception.</Body>
      </div>
      <PageFooter pageNum={2} />
    </div>

    {/* ── PAGE 3 ── */}
    <div ref={page3Ref} className="receipt-card" style={pageStyle}>
      <PageHeader />
      <div style={{ flex: 1 }}>
        <SubTitle>Installation Requirement</SubTitle>
        <Body>The purchased part must be installed within <strong>10-15 days of delivery</strong>. If installation occurs after the warranty period has expired, the warranty will be considered void, regardless of the situation.</Body>
        
        <SubTitle>Post-Warranty Claims</SubTitle>
        <Body>Any issues reported after the warranty period has ended are not eligible for coverage. No claims, returns, or replacements will be accepted once the warranty expires.</Body>
        
        <Body>Customers interested in additional or extended warranty coverage must contact their sales representative right away after placing the order. Any extended warranty must be discussed, agreed upon, and documented in advance.</Body>
        
        <SectionTitle>Modified, Altered, or Tested Parts - No Return Policy</SectionTitle>
        <Body>Any part that has been altered, modified, disassembled, tampered with, or removed for testing purposes is <strong>strictly non-returnable and non-refundable</strong>. Customers must notify and receive approval from Premium Used Auto Parts before performing any testing, disassembly, or modification. Failure to obtain prior authorization will automatically void the warranty and make the part ineligible for return, refund, or exchange.</Body>
        
        <Body>This includes:</Body>
        <BulletList items={[
          'Opening, dismantling, or internally inspecting the part',
          'Tampering with seals, markings, or identification labels',
          'Any physical or electronic modifications',
          'Bench testing, swapping, or trial installation',
          'Attempts to repair, rebuild, or otherwise alter the original condition of the part',
        ]} />
        
        <Body>All parts are uniquely marked and documented prior to shipment. If a returned part shows any signs of modification, disassembly, testing, or tampering, the return will be immediately rejected. No refund, exchange, or warranty claim will be honored.</Body>

        <SectionTitle>Return & Refund Policy</SectionTitle>
        
        <SubTitle>Condition of Returned Items</SubTitle>
        <Body>All returned items must:</Body>
        <BulletList items={[
          'Be in the exact condition in which they were delivered',
          'Match the original item sold (each part is uniquely marked for identification)',
          'Show no signs of damage, alteration, or tampering',
        ]} />
        
        <Body>Returns that do not meet these conditions will be rejected. The only exception applies to items verified as damaged during shipping by a Premium Used Auto Parts representative.</Body>
        
        <SubTitle>Reporting Issues</SubTitle>
        <Body>All concerns must be submitted in writing via mail, email, or fax. This documentation is required to determine whether the issue involves a defect or another part-related concern.</Body>
      </div>
      <PageFooter pageNum={3} />
    </div>

    {/* ── PAGE 4 ── */}
    <div ref={page4Ref} className="receipt-card" style={pageStyle}>
      <PageHeader />
      <div style={{ flex: 1 }}>
        <SubTitle>Damaged or Defective Parts</SubTitle>
        <Body>If a part arrives damaged or defective, the customer must contact Customer Care at <strong>1-800-606-1107</strong> within <strong>24-48 hours</strong> of receiving the shipment.</Body>
        
        <Body>Do not accept visibly damaged packages from the carrier. Refusing the shipment at delivery helps protect your claim.</Body>
        
        <SubTitle>Return Merchandise Authorization (RMA)</SubTitle>
        <Body>An approved Return Merchandise Authorization (RMA) is required before sending any item back.</Body>
        
        <Body>Returns submitted without prior RMA approval will not be accepted.</Body>
        
        <SubTitle>1. Defective (Internally Damaged) Parts</SubTitle>
        <Body>If the part is confirmed to be internally defective, we will provide a return shipping label at our expense.</Body>
        
        <SubTitle>2. Non-Defective Returns (Personal Reasons)</SubTitle>
        <Body>If the return is not due to an internal defect (for example, ordered incorrectly or no longer needed), the customer is responsible for return shipping costs and a <strong>25% restocking fee</strong> plus one-way original shipping charges will apply.</Body>
        
        <Body>If the customer requests that we provide a return shipping label for a non-defective return, the cost of two-way shipping plus a <strong>25% restocking fee</strong> will be deducted after the unit is received and inspected.</Body>
        
        <SubTitle>10. Refused Deliveries</SubTitle>
        <Body>If a customer refuses delivery of a shipment or fails to respond to delivery attempts by the shipping carrier, the customer shall be responsible for a <strong>25% restocking fee</strong> plus all <strong>two-way shipping charges</strong>.</Body>
        
        <SubTitle>11. Order Cancellations (Replacement Declined)</SubTitle>
        <Body>If a customer cancels an order and does not wish to receive a replacement part, Premium Used Auto Parts reserves the right to charge a <strong>25% restocking fee</strong> in addition to applicable two way shipping costs.</Body>
        
        <SubTitle>12. Warranty Assurance</SubTitle>
        <Body>Premium Used Auto Parts stands behind its warranty and aims to ensure customer satisfaction when purchasing used auto parts. For assistance, please contact Premium Used Auto Parts.</Body>
        
        <Body>If the customer files a dispute for the following terms, he or she is responsible:</Body>
        
        <BulletList items={[
          'The customer wants to cancel the order. Since there will be a 25% restocking fee, the customer might negotiate or agree with us. We will not lose the dispute after approval if the customer files a dispute.',
          'If the customer files a dispute without notifying us, does not approach us to resolve the issue, or falsely states any reason without the knowledge of Premium Used Auto Parts, we will not be held accountable.',
          'If the customer files a dispute while the part is still in their possession, which is not acceptable to us, we will only issue a refund after the part is shipped back. The part must meet our inspection standards, and only then will we resolve the issue. Until we receive our part back, we will not lose the dispute.',
          'If the customer files a dispute while keeping our part in their possession and obtains a better or cheaper price from a different company, merchant, salvage yard, or shop, we will not accept the dispute.',
          'The customer must provide a computerized diagnostic report for mechanical parts and pictures or videos for electrical parts. If the customer fails to submit the requested documents or proof and files a dispute, we will not accept the dispute. Contact us at 1-800-606-1107.',
        ]} />
      </div>
      <PageFooter pageNum={4} />
    </div>

    {/* ── PAGE 5 ── */}
    <div ref={page5Ref} className="receipt-card" style={pageStyle}>
      <PageHeader />
      <div style={{ flex: 1 }}>
        <Body>If an issue is found after the {currentLeadData?.vehicleInfo?.part || 'part'} has been installed, the customer must provide:</Body>
        <BulletList items={[
          'A certified diagnostic report from a licensed repair facility.',
          'A complete installation report.',
          'Copies of receipts showing that all required components and necessary maintenance items were replaced.',
          'Clear photographs of the part must also be submitted.',
        ]} />
        
        <Body>Failure to provide any of the required documentation, receipts, or photographs will result in immediate denial of the warranty claim. The {currentLeadData?.vehicleInfo?.part || 'part'} must not be removed from the vehicle without prior written authorization from Premium Used Auto Parts. If the part is removed without our knowledge and approval, the warranty will be immediately voided.</Body>
        
        <Body>If the claim is approved within the warranty period and the part is confirmed defective, we will issue a shipping label for return. Once the part is received and inspected, we will proceed accordingly. If the customer chooses a refund, a <strong>25% restocking fee</strong> and <strong>two-way shipping costs</strong> will be deducted. If the customer prefers a replacement, the replacement will be shipped to the same address only after the original unit has been received and inspected.</Body>
        
        <SectionTitle>Labor Costs – No Coverage</SectionTitle>
        <Body>Premium Used Auto Parts does not cover, reimburse, or assume responsibility for any labor costs under any circumstances. This includes:</Body>
        
        <BulletList items={[
          'Installation costs',
          'Removal costs',
          'Diagnostic fees',
          'Towing charges',
          'Reinstallation expenses',
          'Programming or calibration fees',
          'Any related shop supplies or service charges',
        ]} />
        
        <Body>All labor costs are the sole responsibility of the customer. If a customer raises a dispute or claim stating that labor costs were not paid or reimbursed, Premium Used Auto Parts will not be responsible for such charges. By purchasing and installing the part, the customer acknowledges and agrees that no labor costs will be covered under any circumstances.</Body>
        
        <Body>If a customer refuses to return the unit after initiating a claim and raises a dispute regarding unpaid labor costs, Premium Used Auto Parts will not be responsible for any labor charges under any circumstances. We do not provide any warranty or reimbursement for labor costs. Failure to return the unit when required may result in denial of the warranty claim.</Body>
      </div>
      <PageFooter pageNum={5} />
    </div>

    {/* ── PAGE 6 ── */}
    <div ref={page6Ref} className="receipt-card" style={pageStyle}>
      <PageHeader />
      <div style={{ flex: 1 }}>
        <SectionTitle>Required Replacement Components (Not Covered Under Warranty)</SectionTitle>
        <Body>The following components must be replaced during installation. These items are considered standard installation parts and are not covered under this warranty. We will not pay for these parts or associated labor.</Body>
        
        <Body>To maintain warranty eligibility, the following must be completed and documented at the time of installation:</Body>
        
        <BulletList items={[
          'Replace all external seals and gaskets.',
          'Replace any worn bearings or bushings.',
          'Use manufacturer-recommended fluids and filters.',
          'Clean all mating surfaces before installation.',
          'Inspect wiring harnesses and connectors for damage or corrosion.',
          'Ensure proper programming and calibration if required.',
          'Perform a thorough test after installation.',
        ]} />
        
        <Body>Failure to complete and provide proof of the above requirements will void the warranty.</Body>
        
        <SectionTitle>Visual Appearance</SectionTitle>
        <Body>Cosmetic imperfections, surface rust, and scratches are normal for used parts and are not warranty-covered.</Body>
        
        <Body>Parts from 1995 to 2010 may show some surface rust, as they are used and older in age.</Body>
      </div>
      <PageFooter pageNum={6} />
    </div>
  </>
)}
    </>
  );
};

  return (
    <>
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          
          @keyframes fadeOut {
            from {
              opacity: 1;
            }
            to {
              opacity: 0;
            }
          }
          
          @keyframes pulse {
            0% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.05);
            }
            100% {
              transform: scale(1);
            }
          }
          
          .refreshing-indicator {
            animation: pulse 1s infinite;
          }
          
          .ant-drawer-content {
            border-radius: 16px 0 0 16px !important;
            overflow: hidden;
          }
          
          .ant-drawer-header {
            border-bottom: none !important;
            padding: 0 !important;
            background:linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(118, 75, 162) 100%);
            padding-left:39px;
          }
          
          .ant-drawer-body {
            padding: 0 !important;
            background: #f7fafc;
          }
          
          .ant-drawer-footer {
            border-top: 1px solid #e2e8f0 !important;
            padding: 16px 24px !important;
            background: white;
          }
        `}
      </style>

      <Drawer
        title={
          <div style={{
            padding: '20px 24px',
            borderRadius: '16px 16px 0 0',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%',paddingLeft:"30px" }}>
              <Space size="middle">
                <div>
                  <span style={{ fontSize: '18px', fontWeight: 600, color: 'white', display: 'block' }}>
                    Zoho Sign Details
                  </span>
                  {currentLeadData?.orderNumber && (
                    <Tag color="gold" style={{ marginTop: '4px' }}>Order #{currentLeadData.orderNumber}</Tag>
                  )}
                </div>
              </Space>
              
              {(refreshing || isRefreshing) && (
                <Tag icon={<LoadingOutlined />} color="processing" style={{ marginRight: '16px' }}>
                  Refreshing...
                </Tag>
              )}
              
              <Space>
                {!isEditing ? (
                  <>
                    <Button 
                      icon={<EditOutlined />} 
                      onClick={handleEdit}
                      type="primary"
                      ghost
                      style={{ borderColor: 'white', color: 'white' }}
                    >
                      Edit
                    </Button>
                    <Button 
                      type="primary" 
                      icon={<EyeOutlined />} 
                      style={{ background: 'white', borderColor: 'white', color: '#667eea' }}
                      onClick={handlePreviewPDF}
                    >
                      Preview PDF
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      icon={<CloseCircleOutlined />} 
                      onClick={handleCancelEdit}
                      danger
                      ghost
                      style={{ borderColor: 'white', color: 'white' }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      icon={isSaving ? <LoadingOutlined /> : <SaveOutlined />} 
                      onClick={handleSaveEdit}
                      type="primary"
                      disabled={isSaving || isRefreshing}
                      style={{ background: 'white', borderColor: 'white', color: '#667eea' }}
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </>
                )}
              </Space>
            </div>
          </div>
        }
        placement="right"
        width={1000}
        onClose={onClose}
        open={visible}
        closeIcon={
          <Button 
            onClick={onClose}  
            type="primary"
            ghost
            style={{ background: 'white', borderColor: 'white', color: '#667eea', marginLeft: '40px' }}>
            Close
          </Button>
        }
      >
        {isRefreshing ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Spin size="large" tip="Loading lead data..." />
          </div>
        ) : (
          <div style={{ padding: '24px' }}>
            {/* Order Information Card */}
            <div style={styles.sectionCard}>
              <div style={styles.sectionTitle}>
                <NumberOutlined style={{ color: '#667eea' }} />
                Order Information
              </div>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  {renderField('Order Number', currentLeadData?.orderNumber, <NumberOutlined />, 'orderNumber', isEditing ? 'order' : null)}
                </Col>
                <Col span={12}>
                  {renderField('Total Amount', `$${currentLeadData?.totalAmount || currentLeadData?.paidAmount || 0}`, <DollarOutlined />, 'totalAmount', isEditing ? 'order' : null)}
                </Col>
                <Col span={12}>
                  {renderField('Warranty', currentLeadData?.warranty, <TagOutlined />, 'warranty', isEditing ? 'order' : null)}
                </Col>
              </Row>
            </div>

            {/* Vehicle Information Card */}
            <div style={styles.sectionCard}>
              <div style={styles.sectionTitle}>
                <CarOutlined style={{ color: '#667eea' }} />
                Vehicle Information
              </div>
              <Row gutter={[16, 16]}>
                <Col span={6}>{renderField('Year', currentLeadData?.vehicleInfo?.year, null, 'year', isEditing ? 'vehicleInfo' : null)}</Col>
                <Col span={6}>{renderField('Make', currentLeadData?.vehicleInfo?.make, null, 'make', isEditing ? 'vehicleInfo' : null)}</Col>
                <Col span={6}>{renderField('Model', currentLeadData?.vehicleInfo?.model, null, 'model', isEditing ? 'vehicleInfo' : null)}</Col>
                <Col span={6}>{renderField('Part', currentLeadData?.vehicleInfo?.part, null, 'part', isEditing ? 'vehicleInfo' : null)}</Col>
              </Row>
            </div>

            {/* Billing Information Card */}
            <div style={styles.sectionCard}>
              <div style={styles.sectionTitle}>
                <CreditCardOutlined style={{ color: '#667eea' }} />
                Billing Information
              </div>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  {renderField('First Name', firstName, <UserOutlined />, 'firstName', isEditing ? 'personal' : null)}
                </Col>
                <Col span={12}>
                  {renderField('Last Name', lastName, <UserOutlined />, 'lastName', isEditing ? 'personal' : null)}
                </Col>
                <Col span={12}>
                  {renderField('Email', currentLeadData?.personalInfo?.email, <MailOutlined />, 'email', isEditing ? 'personal' : null)}
                </Col>
                <Col span={12}>
                  {renderField('Phone', currentLeadData?.personalInfo?.phoneNumber, <PhoneOutlined />, 'phoneNumber', isEditing ? 'personal' : null)}
                </Col>
                <Col span={24}>
                  {renderField('Street', currentLeadData?.billingAddress?.street, <HomeOutlined />, 'street', isEditing ? 'billingAddress' : null)}
                </Col>
                <Col span={8}>
                  {renderField('City', currentLeadData?.billingAddress?.city, null, 'city', isEditing ? 'billingAddress' : null)}
                </Col>
                <Col span={8}>
                  {renderField('State', currentLeadData?.billingAddress?.state, null, 'state', isEditing ? 'billingAddress' : null)}
                </Col>
                <Col span={8}>
                  {renderField('Zip', currentLeadData?.billingAddress?.zip, null, 'zip', isEditing ? 'billingAddress' : null)}
                </Col>
                <Col span={12}>
                  <div style={styles.infoRow}>
                    <span style={styles.label}><CreditCardOutlined /> Payment Method</span>
                    {isEditing ? (
                      <select
                        style={styles.editInput}
                        value={currentData?.advancePayments?.[0]?.paymentMethod || paymentMethod}
                        onChange={(e) => handlePaymentMethodChange(e.target.value)}
                      >
                        <option value="Card">Card</option>
                        <option value="Payment Link">Payment Link</option>
                        <option value="Check Payment">Check Payment</option>
                        <option value="Zelle">Zelle</option>
                        <option value="CashApp">Cashapp</option>
                        <option value="Venmo">Venmo</option>
                        <option value="Wire Transfer">Wire Transfer</option>
                        <option value="PayPal">PayPal</option>
                        <option value="Pay Tomorrow">Pay Tomorrow</option>
                      </select>
                    ) : (
                      <span style={styles.value}>
                        <Tag color="blue" style={{ margin: 0 }}>{paymentMethod}</Tag>
                      </span>
                    )}
                  </div>
                </Col>
              </Row>

              {paymentMethod === 'Card' && currentLeadData?.advancePayments?.[0]?.cardNumber && (
                <div style={styles.cardDetails}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text strong style={{ color: '#667eea' }}>💳 Card Details</Text>
                    <Row gutter={[16, 8]}>
                      <Col span={12}>
                        <Text type="secondary">Card Holder:</Text>{' '}
                        <Text strong>{currentLeadData.advancePayments[0].cardHolderName}</Text>
                      </Col>
                      <Col span={12}>
                        <Text type="secondary">Card Type:</Text>{' '}
                        <Text strong>{currentLeadData.advancePayments[0].cardType || detectCardType(currentLeadData.advancePayments[0].cardNumber)}</Text>
                      </Col>
                      <Col span={12}>
                        <Text type="secondary">Card Number:</Text>{' '}
                        <Text strong>{currentLeadData.advancePayments[0].cardNumber}</Text>
                      </Col>
                      <Col span={12}>
                        <Text type="secondary">CVV:</Text>{' '}
                        <Text strong>{currentLeadData.advancePayments[0].cvv}</Text>
                      </Col>
                      <Col span={12}>
                        <Text type="secondary">Expiration:</Text>{' '}
                        <Text strong>{currentLeadData.advancePayments[0].expirationDate}</Text>
                      </Col>
                    </Row>
                  </Space>
                </div>
              )}
            </div>

            {/* Shipping Information Card */}
            <div style={styles.sectionCard}>
              <div style={styles.sectionTitle}>
                <EnvironmentOutlined style={{ color: '#667eea' }} />
                Shipping Information
              </div>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  {renderField('Business Name', currentLeadData?.bussinessName, <ShopOutlined />, 'bussinessName', isEditing ? 'order' : null)}
                </Col>
                <Col span={12}>
                  {renderField('ATTN Name', currentLeadData?.attnName || currentLeadData?.personalInfo?.fullName || `${firstName} ${lastName}`.trim(), <UserOutlined />, 'attnName', isEditing ? 'order' : null)}
                </Col>
                <Col span={24}>
                  {renderField('Street', currentLeadData?.shippingAddress?.street, <HomeOutlined />, 'street', isEditing ? 'shippingAddress' : null)}
                </Col>
                <Col span={8}>
                  {renderField('City', currentLeadData?.shippingAddress?.city, null, 'city', isEditing ? 'shippingAddress' : null)}
                </Col>
                <Col span={8}>
                  {renderField('State', currentLeadData?.shippingAddress?.state, null, 'state', isEditing ? 'shippingAddress' : null)}
                </Col>
                <Col span={8}>
                  {renderField('Zip', currentLeadData?.shippingAddress?.zip, null, 'zip', isEditing ? 'shippingAddress' : null)}
                </Col>
              </Row>
            </div>

            {/* Created Date */}
            <div style={{ textAlign: 'right', marginTop: '16px' }}>
              <Tag icon={<CalendarOutlined />} color="default">
                Created: {new Date(currentLeadData?.createdAt).toLocaleString()}
              </Tag>
            </div>
          </div>
        )}
      </Drawer>
{showPdfViewer && currentLeadData && (
  <div className="pdf-viewer-modal" 
  style={{ 
      zIndex: '999999 !important',  
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0 
    }} 
  onClick={() => setShowPdfViewer(false)}>
    <div className="pdf-viewer-content" onClick={e => e.stopPropagation()}>
      <div className="pdf-viewer-header">
        <h3>PDF Preview - Order #{currentLeadData.orderNumber}</h3>
        <button className="close-button" onClick={() => setShowPdfViewer(false)}>
          <X size={24} />
        </button>
      </div>
      <div className="pdf-viewer-body" id="pdf-viewer-content">
        {PDFViewerContent()}
      </div>
    </div>
  </div>
)}
      {/* Popup Message */}
      {popup.show && (
        <PopupMessage
          message={popup.message}
          type={popup.type}
          onClose={hidePopup}
        />
      )}
         <style>
        {`
        /* Popup animation */
@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOut {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}

          .glass-card {
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(10px);
            border-radius: 20px;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
            transition: transform 0.3s ease;
          }
          
          .glass-card:hover {
            transform: translateY(-5px);
          }
          
          .lead-details {
            border-left: 4px solid #667eea;
            animation: slideIn 0.5s ease;
          }
          
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          .badge-part {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 8px 16px;
            border-radius: 30px;
            font-size: 0.9rem;
            font-weight: 600;
          }
          
          .loading-spinner {
            display: inline-block;
            width: 20px;
            height: 20px;
            border: 3px solid #f3f3f3;
            border-top: 3px solid #667eea;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .form-control-sm {
            font-size: 0.875rem;
            padding: 0.25rem 0.5rem;
          }

          .is-invalid {
            border-color: #dc3545;
          }
          
          .is-invalid:focus {
            box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
          }

          .custom-select-wrapper {
            position: relative;
            width: 100%;
          }

          .custom-select-field {
            width: 100%;
            padding: 12px 40px 12px 16px;
            border: 2px solid #e0e0e0;
            border-radius: 12px;
            background: white;
            font-size: 1rem;
            transition: all 0.3s ease;
            appearance: none;
            cursor: pointer;
            color: #2d3748;
          }

          .custom-select-field:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
            outline: none;
          }

          .custom-select-field:hover {
            border-color: #667eea;
          }

          .custom-select-field:disabled {
            background-color: #f8f9fa;
            cursor: not-allowed;
          }

          .select-arrow {
            position: absolute;
            right: 16px;
            top: 50%;
            transform: translateY(-50%);
            font-size: 14px;
            color: #667eea;
            pointer-events: none;
          }

          /* PDF Viewer Modal Styles */
          .pdf-viewer-modal {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.8);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
            padding: 20px;
          }

          .pdf-viewer-content {
            background: white;
            border-radius: 12px;
            width: 100%;
            height: 90vh;
            display: flex;
            flex-direction: column;
            overflow: hidden;
          }

          .pdf-viewer-header {
            padding: 16px 20px;
            border-bottom: 1px solid #e0e0e0;
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: #f8f9fa;
          }

          .pdf-viewer-header h3 {
            margin: 0;
            color: #2d3748;
            font-size: 1.2rem;
          }

          .pdf-viewer-body {
            flex: 1;
            overflow-y: auto;
            padding: 30px;
            background: #525659;
          }

          .pdf-page {
            background: white;
            width: 8.5in;
            margin: 0 auto 20px;
            padding: 0.5in;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
            border-radius: 4px;
            font-family: Arial, sans-serif;
            line-height: 1.6;
            color: #333;
          }

          .pdf-page:last-child {
            margin-bottom: 0;
          }

          .close-button {
            background: none;
            border: none;
            font-size: 24px;
            cursor: pointer;
            color: #667eea;
            padding: 4px 8px;
            border-radius: 4px;
          }

          .close-button:hover {
            background: rgba(102, 126, 234, 0.1);
          }

          /* PDF Content Styles */
          #pdf-viewer-content h2,
          #pdf-viewer-content h3 {
            color: #2f5496;
          }
          
          #pdf-viewer-content h3 {
            border-bottom: 2px solid #2f5496;
            padding-bottom: 5px;
          }
          
          #pdf-viewer-content .signature {
            margin-top: 40px;
            display: flex;
            justify-content: space-between;
          }

          .pdf-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #2f5496;
          }

          .pdf-footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #e0e0e0;
            text-align: center;
          }
        `}
      </style>
    </>
    
  );
};

export default LeadDetailsDrawer;
